import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useGamesBase } from '../../utils/gamesBase';
import apiClient from '../../utils/apiClient';
import { useAuth } from '../../contexts/AuthContext';

// Game constants
const GAME_CONFIG = {
  // Starting values
  startHeading: 0,
  startAltitude: 5000,
  startSpeed: 150,
  startThrust: 10,
  
  // Limits
  maxHeadingRate: 1740, // degrees per minute
  headingAcceleration: 870, // degrees per minute per second (reaches max in 2 seconds)
  maxVerticalSpeed: 3050, // fpm
  verticalSpeedRate: 3050, // fpm per second of input
  thrustLevels: 20,
  thrustSpeedEffect: 3.75, // knots per thrust increment
  thrustVerticalEffect: 610, // fpm per thrust increment deviation from mid
  
  // Target movement rates (for changing mode)
  headingTargetRate: 420, // degrees per minute
  altitudeTargetRate: 1300, // fpm
  
  // Turbulence (min to max range)
  turbulenceHeadingMin: 189, // minimum degrees per minute
  turbulenceHeadingMax: 200, // maximum degrees per minute
  turbulenceAltitudeMin: 450, // minimum fpm
  turbulenceAltitudeMax: 500, // maximum fpm
  
  // Speed table based on thrust and vertical speed
  speedTable: {
    idle_climb: 90,
    idle_level: 117,
    mid_climb: 128,
    idle_descent: 135,
    mid_level: 150,
    max_climb: 165,
    mid_descent: 175,
    max_level: 185,
    max_descent: 230
  }
};

const MICGame = () => {
  // Debug overlays reveal answers; only staff may toggle them (Shift+O)
  const { user } = useAuth();
  const isStaff = !!(user?.is_staff || user?.is_superuser);
  const { subjectId } = useParams();
  const navigate = useNavigate();
  const gamesBase = useGamesBase();
  const location = useLocation();
  const settings = location.state?.settings;
  
  // Canvas refs
  const compassCanvasRef = useRef(null);
  const altimeterCanvasRef = useRef(null);
  const speedCanvasRef = useRef(null);
  const clockCanvasRef = useRef(null);
  
  // Game state
  const [gameState, setGameState] = useState('briefing'); // 'briefing', 'playing', 'finished'
  const [briefingCountdown, setBriefingCountdown] = useState(10);
  const [showExitConfirm, setShowExitConfirm] = useState(false); // Exit confirmation modal
  const [joystickConnected, setJoystickConnected] = useState(false); // Joystick connection status
  const joystickRef = useRef(null); // Store joystick gamepad index
  const isRealTestMode = settings?.runMode === 'real_test';
  const REAL_TEST_TASK_DURATION = 75; // 75 seconds per task in real test mode
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0); // For real test mode - which task we're on (0-11)
  const [realTestResults, setRealTestResults] = useState([]); // Store results for each task in real test mode
  const briefingTimerRef = useRef(null); // Ref to track briefing timer
  
  // Calculate initial time based on mode
  const getTaskDuration = () => {
    if (isRealTestMode) return REAL_TEST_TASK_DURATION;
    return settings?.duration || 180; // duration is already in seconds from setup
  };
  const [timeRemaining, setTimeRemaining] = useState(getTaskDuration());
  const [isPaused, setIsPaused] = useState(false);
  
  // Flight state - use refs for physics, state for rendering
  const flightRef = useRef({
    heading: GAME_CONFIG.startHeading,
    altitude: GAME_CONFIG.startAltitude,
    speed: GAME_CONFIG.startSpeed,
    thrust: GAME_CONFIG.startThrust,
    headingRate: 0,
    verticalSpeed: 0,
    displayVS: 0,
    targetHeading: GAME_CONFIG.startHeading,
    targetAltitude: GAME_CONFIG.startAltitude,
    targetSpeed: GAME_CONFIG.startSpeed,
    inputX: 0,
    inputY: 0,
    clockElapsed: 0 // Smooth clock elapsed time in seconds
  });
  
  // State for rendering (updated at lower frequency)
  const [flightState, setFlightState] = useState({
    heading: GAME_CONFIG.startHeading,
    altitude: GAME_CONFIG.startAltitude,
    speed: GAME_CONFIG.startSpeed,
    headingRate: 0,
    verticalSpeed: 0,
    displayVS: 0,
    targetHeading: GAME_CONFIG.startHeading,
    targetAltitude: GAME_CONFIG.startAltitude,
    targetSpeed: GAME_CONFIG.startSpeed,
    inputX: 0,
    inputY: 0
  });
  const [thrust, setThrust] = useState(GAME_CONFIG.startThrust);
  const [displayThrust, setDisplayThrust] = useState(GAME_CONFIG.startThrust); // For throttle bar display
  
  // Throttle delay - stores pending thrust changes with timestamps
  const throttleDelayRef = useRef({
    pendingThrust: GAME_CONFIG.startThrust,
    effectiveThrust: GAME_CONFIG.startThrust,
    currentThrustEffect: 0, // Gradual thrust effect (transitions smoothly)
    lastChangeTime: 0
  });
  const THROTTLE_DELAY_MS = 1000; // 1 second delay
  const THRUST_EFFECT_RATE = 20; // fpm per second per thrust increment (same speed as airspeed)
  
  // Destructure for easier access
  const { heading, altitude, speed, headingRate, verticalSpeed, displayVS, targetHeading, targetAltitude, targetSpeed, inputX, inputY } = flightState;
  
  const keysPressed = useRef({ left: false, right: false, up: false, down: false });
  
  // Visual feedback for keyboard shortcuts - tracks which buttons appear "pressed"
  const [throttlePlusPressed, setThrottlePlusPressed] = useState(false);
  const [throttleMinusPressed, setThrottleMinusPressed] = useState(false);
  const [redButtonPressed, setRedButtonPressed] = useState(false);
  const [greenButtonPressed, setGreenButtonPressed] = useState(false);
  
  // Auditory task state
  const [audioEnabled, setAudioEnabled] = useState(settings?.audio || false);
  
  // Turbulence state - also sync to ref for physics loop access
  const [turbulenceEnabled, setTurbulenceEnabled] = useState(settings?.turbulence || false);
  
  // Sync settings when they change (e.g., from custom settings)
  useEffect(() => {
    if (settings?.audio !== undefined) {
      setAudioEnabled(settings.audio);
    }
    if (settings?.turbulence !== undefined) {
      setTurbulenceEnabled(settings.turbulence);
    }
  }, [settings?.audio, settings?.turbulence]);
  
  // Keep turbulence ref in sync with state
  useEffect(() => {
    turbulenceRef.current.enabled = turbulenceEnabled;
  }, [turbulenceEnabled]);
  const [numberSequence, setNumberSequence] = useState([]);
  const [auditoryResults, setAuditoryResults] = useState({ correct: 0, missed: 0, falsePositives: 0 });
  const [expectedButton, setExpectedButton] = useState(null); // 'red' for odd, 'green' for even, null for none
  const [auditoryFeedback, setAuditoryFeedback] = useState(null); // { type: 'missed'|'incorrect', message: string }
  const [auditoryStats, setAuditoryStats] = useState({ expectedSoFar: 0, totalExpected: 0 }); // Track expected responses
  const auditoryResultsRef = useRef({ correct: 0, missed: 0, falsePositives: 0 }); // Ref to track current auditory results for callbacks
  const auditoryStatsRef = useRef({ expectedSoFar: 0, totalExpected: 0 }); // Ref to track current auditory stats for callbacks
  
  // Keep auditory refs in sync with state (refs always have latest values for callbacks)
  useEffect(() => {
    auditoryResultsRef.current = auditoryResults;
  }, [auditoryResults]);
  
  useEffect(() => {
    auditoryStatsRef.current = auditoryStats;
  }, [auditoryStats]);
  
  const expectedButtonRef = useRef(null); // Track if user responded to current sequence
  const lastSequenceTypeRef = useRef(null); // Track last sequence type to prevent consecutive same-type
  const handleAuditoryResponseRef = useRef(null); // Ref to access handleAuditoryResponse in keyboard effect
  const speechSynthRef = useRef(null);
  const [preGeneratedSequence, setPreGeneratedSequence] = useState([]); // Pre-generated sequence for debug display
  const [currentNumberIndex, setCurrentNumberIndex] = useState(0); // Track which number we're on
  
  // Deviation tracking - sample every second, not every frame
  const deviationData = useRef([]);
  const gameStartTime = useRef(null);
  const lastDeviationSample = useRef(0);
  
  // FPS tracking
  const fpsRef = useRef({ frames: 0, lastTime: Date.now(), fps: 0 });
  
  // Clock tracking - use a dedicated start time that persists
  const clockStartTime = useRef(null);
  
  // Turbulence ref - to access current state in physics loop
  // Pattern: random value -> -2x that value -> new random value (3-phase cycle)
  const turbulenceRef = useRef({ 
    enabled: settings?.turbulence || false,
    lastUpdate: 0,
    headingOffset: 0,
    altitudeOffset: 0,
    phase: 0,            // 0 = random, 1 = -2x previous, 2 = new random (then back to 0)
    savedHeadingMag: 0,  // Store the random value from phase 0
    savedAltitudeMag: 0,
    nextUpdateDelay: 500 // Variable delay between 250-1000ms
  });
  
  // Target direction ref - for randomizing heading/altitude target movement direction
  // Direction changes limited to max 2 per leg when in changing mode
  const targetDirectionRef = useRef({
    headingDir: Math.random() < 0.5 ? 1 : -1,  // 1 = clockwise, -1 = counter-clockwise
    altitudeDir: Math.random() < 0.5 ? 1 : -1, // 1 = climb, -1 = descend
    lastHeadingChange: 0,
    lastAltitudeChange: 0,
    nextHeadingChangeDelay: 5000 + Math.random() * 10000, // 5-15 seconds
    nextAltitudeChangeDelay: 5000 + Math.random() * 10000,
    headingChangeCount: 0,  // Track number of direction changes per leg (max 2)
    altitudeChangeCount: 0  // Track number of direction changes per leg (max 2)
  });
  
  // Debug mode - hidden by default, toggle with Shift+O
  const [debugMode, setDebugMode] = useState(false);
  
  // Virtual joystick - hidden by default, toggle with button or auto-enable on touch devices
  const [showVirtualJoystick, setShowVirtualJoystick] = useState(false);

  // Instruments sit at fixed pixel offsets from the centre (real DLR layout), spanning
  // 466px left / 398px right / 355px up. Scale the whole panel down when the screen is smaller.
  const panelRef = useRef(null);
  const [panelScale, setPanelScale] = useState(1);
  const [showSmallScreenTip, setShowSmallScreenTip] = useState(() => window.innerWidth < 700);
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return undefined;
    const fit = () => setPanelScale(Math.min(1, (el.offsetWidth - 16) / 932, (el.offsetHeight - 16) / 710));
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [virtualJoystickPos, setVirtualJoystickPos] = useState({ x: 0, y: 0 });
  const virtualJoystickRef = useRef({ x: 0, y: 0, active: false });

  // Detect touch device and auto-enable virtual joystick
  useEffect(() => {
    const checkTouchDevice = () => {
      const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      setIsTouchDevice(isTouch);
      if (isTouch) {
        setShowVirtualJoystick(true);
      }
    };
    checkTouchDevice();
  }, []);

  // Initialize game
  useEffect(() => {
    if (!settings) {
      navigate(`${gamesBase}/mic/${subjectId}`);
      return;
    }
    
    // Start briefing countdown - store in ref so skipBriefing can clear it
    briefingTimerRef.current = setInterval(() => {
      setBriefingCountdown(prev => {
        if (prev <= 1) {
          clearInterval(briefingTimerRef.current);
          briefingTimerRef.current = null;
          setGameState('playing');
          gameStartTime.current = Date.now();
          clockStartTime.current = performance.now(); // Use performance.now() for clock
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => {
      if (briefingTimerRef.current) {
        clearInterval(briefingTimerRef.current);
        briefingTimerRef.current = null;
      }
    };
  }, [settings, navigate, subjectId, gamesBase]);

  // Get current task settings for real test mode
  const getCurrentTaskSettings = useCallback(() => {
    if (!isRealTestMode) return settings;
    const task = PREDEFINED_TASKS[currentTaskIndex];
    return { ...settings, ...task, predefinedTaskNumber: task.id };
  }, [isRealTestMode, currentTaskIndex, settings]);

  // Effective settings - updates based on real test mode and current task
  // This is used by drawing functions to respect task-specific instrument states
  const [effectiveSettings, setEffectiveSettings] = useState(settings);
  const effectiveSettingsRef = useRef(settings); // Ref for physics loop access
  
  useEffect(() => {
    let newSettings;
    if (isRealTestMode) {
      const task = PREDEFINED_TASKS[currentTaskIndex];
      newSettings = { ...settings, ...task };
    } else {
      newSettings = settings;
    }
    setEffectiveSettings(newSettings);
    effectiveSettingsRef.current = newSettings; // Keep ref in sync
  }, [isRealTestMode, currentTaskIndex, settings]);

  // Separate timer for countdown (more reliable than game loop)
  useEffect(() => {
    if (gameState !== 'playing' || isPaused) return;
    
    const timerInterval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          // In real test mode, check if there are more tasks
          if (isRealTestMode && currentTaskIndex < PREDEFINED_TASKS.length - 1) {
            // Save current task results before moving to next
            handleTaskComplete();
            return 0; // Will be reset in handleTaskComplete
          } else {
            setGameState('finished');
            return 0;
          }
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timerInterval);
  }, [gameState, isPaused, isRealTestMode, currentTaskIndex]);

  // Handle completion of a single task in real test mode
  const handleTaskComplete = useCallback(() => {
    const currentTask = PREDEFINED_TASKS[currentTaskIndex];
    
    // Use refs to get current auditory data (refs always have latest values, avoiding stale closure issues)
    const capturedAuditoryResults = { ...auditoryResultsRef.current };
    const capturedAuditoryStats = { ...auditoryStatsRef.current };
    
    // Capture deviation data immediately (it's a ref, so we need to copy it)
    const capturedDeviationData = [...deviationData.current];
    
    // Use the same scoring logic as saveResults
    const headingDeviations = capturedDeviationData.map(d => Math.abs(d.headingDeviation));
    const altitudeDeviations = capturedDeviationData.map(d => Math.abs(d.altitudeDeviation));
    const speedDeviations = capturedDeviationData.map(d => Math.abs(d.speedDeviation));
    
    const avgHeadingDev = headingDeviations.length > 0 ? headingDeviations.reduce((a, b) => a + b, 0) / headingDeviations.length : 0;
    const avgAltitudeDev = altitudeDeviations.length > 0 ? altitudeDeviations.reduce((a, b) => a + b, 0) / altitudeDeviations.length : 0;
    const avgSpeedDev = speedDeviations.length > 0 ? speedDeviations.reduce((a, b) => a + b, 0) / speedDeviations.length : 0;
    
    // Calculate scores with new logic (0% if off, 0.8x if constant)
    let headingScore = 0;
    if (currentTask.compass !== 'off') {
      const rawScore = Math.max(0, 100 - avgHeadingDev * 2);
      headingScore = currentTask.compass === 'constant' ? rawScore * 0.8 : rawScore;
    }
    
    let altitudeScore = 0;
    if (currentTask.altimeter !== 'off') {
      const rawScore = Math.max(0, 100 - avgAltitudeDev / 5);
      altitudeScore = currentTask.altimeter === 'constant' ? rawScore * 0.8 : rawScore;
    }
    
    let speedScore = 0;
    if (currentTask.airspeed) {
      speedScore = Math.max(0, 100 - avgSpeedDev * 2);
    }
    
    let auditoryScore = 0;
    if (currentTask.audio) {
      const totalExpected = capturedAuditoryStats.totalExpected || 0;
      if (totalExpected > 0) {
        auditoryScore = (capturedAuditoryResults.correct / totalExpected) * 100;
      } else {
        auditoryScore = capturedAuditoryResults.correct > 0 ? 100 : 0;
      }
    }
    
    // Final score is average of all 4 components
    const finalScore = (headingScore + altitudeScore + speedScore + auditoryScore) / 4;
    
    // Store task result (use captured data to avoid stale values)
    setRealTestResults(prev => [...prev, {
      taskId: currentTask.id,
      taskTitle: currentTask.title,
      finalScore,
      headingScore,
      altitudeScore,
      speedScore,
      auditoryScore,
      auditoryResults: capturedAuditoryResults,
      auditoryStats: capturedAuditoryStats,
      deviationData: capturedDeviationData
    }]);
    
    // Reset for next task
    deviationData.current = [];
    lastDeviationSample.current = 0; // Reset deviation sampling timer
    setAuditoryResults({ correct: 0, missed: 0, falsePositives: 0 });
    setAuditoryStats({ expectedSoFar: 0, totalExpected: 0 }); // Reset auditory stats
    setNumberSequence([]);
    setPreGeneratedSequence([]);
    setCurrentNumberIndex(0);
    expectedButtonRef.current = null;
    setExpectedButton(null);
    
    // Reset flight state
    const f = flightRef.current;
    f.heading = GAME_CONFIG.startHeading;
    f.altitude = GAME_CONFIG.startAltitude;
    f.speed = GAME_CONFIG.startSpeed;
    f.thrust = GAME_CONFIG.startThrust;
    f.headingRate = 0;
    f.verticalSpeed = 0;
    f.displayVS = 0;
    f.targetHeading = GAME_CONFIG.startHeading;
    f.targetAltitude = GAME_CONFIG.startAltitude;
    f.targetSpeed = GAME_CONFIG.startSpeed;
    f.inputX = 0;
    f.inputY = 0;
    
    // Reset throttle
    throttleDelayRef.current.pendingThrust = GAME_CONFIG.startThrust;
    throttleDelayRef.current.effectiveThrust = GAME_CONFIG.startThrust;
    setThrust(GAME_CONFIG.startThrust);
    setDisplayThrust(GAME_CONFIG.startThrust);
    
    // Reset target direction for next task (randomize again, reset change counters)
    targetDirectionRef.current = {
      headingDir: Math.random() < 0.5 ? 1 : -1,
      altitudeDir: Math.random() < 0.5 ? 1 : -1,
      lastHeadingChange: 0,
      lastAltitudeChange: 0,
      nextHeadingChangeDelay: 5000 + Math.random() * 10000,
      nextAltitudeChangeDelay: 5000 + Math.random() * 10000,
      headingChangeCount: 0,  // Reset for new leg
      altitudeChangeCount: 0  // Reset for new leg
    };
    
    // Move to next task
    setCurrentTaskIndex(prev => prev + 1);
    setBriefingCountdown(10);
    setGameState('briefing');
    setTimeRemaining(REAL_TEST_TASK_DURATION);
  }, [currentTaskIndex]);

  // Update audio/turbulence settings when task changes in real test mode
  useEffect(() => {
    if (isRealTestMode && gameState === 'briefing') {
      const task = PREDEFINED_TASKS[currentTaskIndex];
      setAudioEnabled(task.audio);
      setTurbulenceEnabled(task.turbulence);
    }
  }, [isRealTestMode, currentTaskIndex, gameState]);

  // Handle briefing countdown for subsequent tasks in real test mode
  useEffect(() => {
    // Only run for real test mode when in briefing state (for any task after the first)
    if (!isRealTestMode || gameState !== 'briefing' || currentTaskIndex === 0) return;
    
    // Clear any existing timer
    if (briefingTimerRef.current) {
      clearInterval(briefingTimerRef.current);
    }
    
    briefingTimerRef.current = setInterval(() => {
      setBriefingCountdown(prev => {
        if (prev <= 1) {
          clearInterval(briefingTimerRef.current);
          briefingTimerRef.current = null;
          setGameState('playing');
          gameStartTime.current = Date.now();
          clockStartTime.current = performance.now();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => {
      if (briefingTimerRef.current) {
        clearInterval(briefingTimerRef.current);
        briefingTimerRef.current = null;
      }
    };
  }, [isRealTestMode, gameState, currentTaskIndex]);

  // Function to skip briefing countdown
  const skipBriefing = useCallback(() => {
    if (briefingTimerRef.current) {
      clearInterval(briefingTimerRef.current);
      briefingTimerRef.current = null;
    }
    setBriefingCountdown(0);
    setGameState('playing');
    gameStartTime.current = Date.now();
    clockStartTime.current = performance.now();
  }, []);

  // Joystick detection and input handling
  useEffect(() => {
    const handleGamepadConnected = (e) => {
      joystickRef.current = e.gamepad.index;
      setJoystickConnected(true);
    };
    
    const handleGamepadDisconnected = (e) => {
      if (joystickRef.current === e.gamepad.index) {
        joystickRef.current = null;
        setJoystickConnected(false);
      }
    };
    
    // Check for already connected gamepads
    const gamepads = navigator.getGamepads ? navigator.getGamepads() : [];
    for (const gamepad of gamepads) {
      if (gamepad) {
        joystickRef.current = gamepad.index;
        setJoystickConnected(true);
        break;
      }
    }
    
    window.addEventListener('gamepadconnected', handleGamepadConnected);
    window.addEventListener('gamepaddisconnected', handleGamepadDisconnected);
    
    return () => {
      window.removeEventListener('gamepadconnected', handleGamepadConnected);
      window.removeEventListener('gamepaddisconnected', handleGamepadDisconnected);
    };
  }, []);

  // Joystick input polling
  useEffect(() => {
    if (gameState !== 'playing' || isPaused || !joystickConnected) return;
    
    const DEADZONE = 0.15; // Ignore small movements
    
    const pollGamepad = () => {
      const gamepads = navigator.getGamepads ? navigator.getGamepads() : [];
      const gamepad = gamepads[joystickRef.current];
      
      if (!gamepad) return;
      
      // Axes: typically [0] = left stick X, [1] = left stick Y
      const rawX = gamepad.axes[0] || 0;
      const rawY = gamepad.axes[1] || 0;
      
      // Apply deadzone
      const x = Math.abs(rawX) > DEADZONE ? rawX : 0;
      const y = Math.abs(rawY) > DEADZONE ? rawY : 0;
      
      // Update flight input (joystick overrides keyboard when active)
      if (x !== 0 || y !== 0) {
        flightRef.current.inputX = x;
        flightRef.current.inputY = y;
      }
      
      // Throttle control - buttons or triggers
      // Common mappings: buttons[6]/[7] = triggers, buttons[12-15] = d-pad
      const throttleUp = gamepad.buttons[7]?.pressed || gamepad.buttons[5]?.pressed; // RT or RB
      const throttleDown = gamepad.buttons[6]?.pressed || gamepad.buttons[4]?.pressed; // LT or LB
      
      // Only allow throttle changes if airspeed is enabled (use effectiveSettingsRef for real test mode)
      if (effectiveSettingsRef.current?.airspeed) {
        if (throttleUp) {
          const newThrust = Math.min(20, throttleDelayRef.current.pendingThrust + 0.1);
          throttleDelayRef.current.pendingThrust = Math.round(newThrust);
          throttleDelayRef.current.lastChangeTime = Date.now();
          setThrust(Math.round(newThrust));
        }
        if (throttleDown) {
          const newThrust = Math.max(0, throttleDelayRef.current.pendingThrust - 0.1);
          throttleDelayRef.current.pendingThrust = Math.round(newThrust);
          throttleDelayRef.current.lastChangeTime = Date.now();
          setThrust(Math.round(newThrust));
        }
      }
      
      // Auditory buttons - A = green (even), B/X = red (odd)
      if (gamepad.buttons[0]?.pressed && handleAuditoryResponseRef.current) {
        handleAuditoryResponseRef.current('green'); // A button
      }
      if ((gamepad.buttons[1]?.pressed || gamepad.buttons[2]?.pressed) && handleAuditoryResponseRef.current) {
        handleAuditoryResponseRef.current('red'); // B or X button
      }
    };
    
    const gamepadInterval = setInterval(pollGamepad, 16); // ~60fps
    
    return () => clearInterval(gamepadInterval);
  }, [gameState, isPaused, joystickConnected]);

  // Track last update time for accurate deltaTime
  const lastUpdateTime = useRef(Date.now());
  
  // Game loop - uses refs for physics, batches state updates for rendering
  useEffect(() => {
    if (gameState !== 'playing' || isPaused) return;
    
    lastUpdateTime.current = Date.now();
    const f = flightRef.current;
    
    // Physics loop - runs frequently, updates refs only
    const physicsLoop = setInterval(() => {
      const now = Date.now();
      const deltaTime = (now - lastUpdateTime.current) / 1000;
      lastUpdateTime.current = now;
      
      // Skip if deltaTime is too large (tab was inactive)
      if (deltaTime > 0.1) return;
      
      // Throttle delay - after 1 second, start transitioning to pending thrust
      const td = throttleDelayRef.current;
      if (td.pendingThrust !== td.effectiveThrust) {
        if (now - td.lastChangeTime >= THROTTLE_DELAY_MS) {
          // Start smooth transition - effectiveThrust becomes the target
          td.effectiveThrust = td.pendingThrust;
        }
      }
      
      // Smooth thrust transition - f.thrust gradually moves toward effectiveThrust
      // This controls throttle bar fill AND affects airspeed/altimeter simultaneously
      const thrustDiff = td.effectiveThrust - f.thrust;
      const thrustChangeRate = 1 * deltaTime; // 1 increment per second (smooth fill)
      if (Math.abs(thrustDiff) < thrustChangeRate) {
        f.thrust = td.effectiveThrust;
      } else {
        f.thrust += thrustDiff > 0 ? thrustChangeRate : -thrustChangeRate;
      }
      
      // Thrust effect on VS - based on current (smoothly transitioning) thrust
      const es = effectiveSettingsRef.current; // Use effective settings for physics
      const currentThrustEffect = es?.airspeed && es?.airspeed !== 'off' ? (f.thrust - 10) * GAME_CONFIG.thrustVerticalEffect : 0;
      td.currentThrustEffect = currentThrustEffect;
      
      // HEADING RATE: X-axis controls acceleration (only if compass is active)
      if (es?.compass !== 'off') {
        const headingAccel = GAME_CONFIG.headingAcceleration * deltaTime * f.inputX;
        f.headingRate = Math.max(-GAME_CONFIG.maxHeadingRate, 
          Math.min(GAME_CONFIG.maxHeadingRate, f.headingRate + headingAccel));
      } else {
        f.headingRate = 0; // No heading changes when compass is off
      }
      
      // Turbulence - 3-phase pattern with variable timing
      // Phase 0: +X for random time
      // Phase 1: -2X for random time  
      // Phase 2: 0 for a brief moment (50-100ms), then back to phase 0 with new random
      const turb = turbulenceRef.current;
      if (turb.enabled && now - turb.lastUpdate >= turb.nextUpdateDelay) {
        let headingTarget, altitudeTarget;
        
        if (turb.phase === 0) {
          // Phase 0: Generate new random value (positive)
          const headingMag = GAME_CONFIG.turbulenceHeadingMin + Math.random() * (GAME_CONFIG.turbulenceHeadingMax - GAME_CONFIG.turbulenceHeadingMin);
          const altitudeMag = GAME_CONFIG.turbulenceAltitudeMin + Math.random() * (GAME_CONFIG.turbulenceAltitudeMax - GAME_CONFIG.turbulenceAltitudeMin);
          turb.savedHeadingMag = headingMag;
          turb.savedAltitudeMag = altitudeMag;
          headingTarget = headingMag;
          altitudeTarget = altitudeMag;
          turb.phase = 1;
          turb.nextUpdateDelay = 250 + Math.random() * 750;
        } else if (turb.phase === 1) {
          // Phase 1: Negative 2x the saved value
          headingTarget = -2 * turb.savedHeadingMag;
          altitudeTarget = -2 * turb.savedAltitudeMag;
          turb.phase = 2;
          turb.nextUpdateDelay = 250 + Math.random() * 750;
        } else {
          // Phase 2: Return to 0 briefly, then back to phase 0
          headingTarget = 0;
          altitudeTarget = 0;
          turb.phase = 0;
          turb.nextUpdateDelay = 50 + Math.random() * 50; // Brief pause (50-100ms)
        }
        
        // SET turbulence offset (not add)
        turb.headingOffset = headingTarget;
        turb.altitudeOffset = altitudeTarget;
        turb.lastUpdate = now;
      }
      
      // Turbulence offset is applied separately when updating heading (not to headingRate)
      
      // VERTICAL SPEED: affected by both Y-axis input and gradual thrust effect (only if altimeter is active)
      if (es?.altimeter !== 'off') {
        // Stick input changes VS (rate of change)
        const vsChange = GAME_CONFIG.verticalSpeedRate * deltaTime * f.inputY;
        f.verticalSpeed = f.verticalSpeed + vsChange;
        
        // Use gradual thrust effect instead of instant
        const thrustOffset = td.currentThrustEffect;
        const finalVS = f.verticalSpeed + thrustOffset;
        
        // Clamp the final VS (with thrust offset) to ±3050 fpm
        // But store the base VS (without thrust) so thrust can be changed independently
        if (finalVS > GAME_CONFIG.maxVerticalSpeed) {
          f.verticalSpeed = GAME_CONFIG.maxVerticalSpeed - thrustOffset;
        } else if (finalVS < -GAME_CONFIG.maxVerticalSpeed) {
          f.verticalSpeed = -GAME_CONFIG.maxVerticalSpeed - thrustOffset;
        }
      } else {
        f.verticalSpeed = 0; // No altitude changes when altimeter is off
      }
      
      // Turbulence altitude offset is applied separately when updating altitude (not to verticalSpeed)
      
      // Update heading - include turbulence offset separately (not accumulated in headingRate)
      // Only apply if compass is active
      if (es?.compass !== 'off') {
        const turbHeadingOffset = turb.enabled ? (turb.headingOffset / 60) * deltaTime : 0;
        f.heading += (f.headingRate / 60) * deltaTime + turbHeadingOffset;
        while (f.heading < 0) f.heading += 360;
        while (f.heading >= 360) f.heading -= 360;
      }
      
      // Update altitude based on VS (including gradual thrust offset and turbulence)
      // Only apply if altimeter is active
      if (es?.altimeter !== 'off') {
        const displayVS = Math.max(-GAME_CONFIG.maxVerticalSpeed, Math.min(GAME_CONFIG.maxVerticalSpeed, f.verticalSpeed + td.currentThrustEffect));
        f.displayVS = displayVS; // Store for UI display
        const turbAltitudeOffset = turb.enabled ? (turb.altitudeOffset / 60) * deltaTime : 0;
        f.altitude = Math.max(0, Math.min(99999, f.altitude + (displayVS / 60) * deltaTime + turbAltitudeOffset));
      } else {
        f.displayVS = 0; // No VS display when altimeter is off
      }
      
      // Update speed only if ASI is enabled
      // Speed = base + thrust effect + VS effect
      // Base: 150 kts at mid thrust (10) and level flight
      // Thrust effect: +/-3.75 kts per increment from mid (10)
      // VS effect: -0.0072131148 kts per fpm (instant) - climbing decreases speed, descending increases
      if (es?.airspeed && es?.airspeed !== 'off') {
        const thrustEffect = (f.thrust - 10) * 3.75; // +/-3.75 kts per increment from mid
        const vsEffect = -f.displayVS * 0.0072131148; // +3050 fpm = -22 kts, -3050 fpm = +22 kts
        const targetSpd = 150 + thrustEffect + vsEffect;
        // Instant response - no gradual transition for speed
        f.speed = Math.max(40, Math.min(260, targetSpd));
      }
      
      // Update targets if changing mode - with random direction changes
      const tDir = targetDirectionRef.current;
      const nowMs = Date.now();
      
      if (es?.compass === 'changing') {
        // Check if it's time to randomly change heading direction (max 2 changes per leg)
        if (tDir.headingChangeCount < 2 && nowMs - tDir.lastHeadingChange >= tDir.nextHeadingChangeDelay) {
          tDir.headingDir = Math.random() < 0.5 ? 1 : -1;
          tDir.lastHeadingChange = nowMs;
          tDir.nextHeadingChangeDelay = 5000 + Math.random() * 10000; // 5-15 seconds
          tDir.headingChangeCount++;
        }
        
        f.targetHeading += tDir.headingDir * (GAME_CONFIG.headingTargetRate / 60) * deltaTime;
        // Normalize heading to 0-360
        while (f.targetHeading >= 360) f.targetHeading -= 360;
        while (f.targetHeading < 0) f.targetHeading += 360;
      }
      
      if (es?.altimeter === 'changing') {
        // Check if it's time to randomly change altitude direction (max 2 changes per leg)
        if (tDir.altitudeChangeCount < 2 && nowMs - tDir.lastAltitudeChange >= tDir.nextAltitudeChangeDelay) {
          tDir.altitudeDir = Math.random() < 0.5 ? 1 : -1;
          tDir.lastAltitudeChange = nowMs;
          tDir.nextAltitudeChangeDelay = 5000 + Math.random() * 10000; // 5-15 seconds
          tDir.altitudeChangeCount++;
        }
        
        f.targetAltitude += tDir.altitudeDir * (GAME_CONFIG.altitudeTargetRate / 60) * deltaTime;
        // Keep altitude in reasonable bounds (1000-10000)
        if (f.targetAltitude <= 1000) {
          f.targetAltitude = 1000;
          tDir.altitudeDir = 1; // Force climb when hitting floor (doesn't count as direction change)
        }
        if (f.targetAltitude >= 10000) {
          f.targetAltitude = 10000;
          tDir.altitudeDir = -1; // Force descent when hitting ceiling (doesn't count as direction change)
        }
      }
    }, 16); // ~60fps physics
    
    // Render loop - updates state less frequently for UI
    const renderLoop = setInterval(() => {
      const f = flightRef.current;
      
      // Update FPS counter
      fpsRef.current.frames++;
      const now = Date.now();
      if (now - fpsRef.current.lastTime >= 1000) {
        fpsRef.current.fps = fpsRef.current.frames;
        fpsRef.current.frames = 0;
        fpsRef.current.lastTime = now;
      }
      
      setFlightState({
        heading: f.heading,
        altitude: f.altitude,
        speed: f.speed,
        headingRate: f.headingRate,
        verticalSpeed: f.verticalSpeed,
        displayVS: f.displayVS,
        targetHeading: f.targetHeading,
        targetAltitude: f.targetAltitude,
        targetSpeed: f.targetSpeed,
        inputX: f.inputX,
        inputY: f.inputY
      });
      
      // Update displayThrust to show current thrust (smoothly transitioning)
      setDisplayThrust(f.thrust);
      
      // Record deviation data - only once per second to avoid memory bloat
      if (gameStartTime.current) {
        const elapsed = (Date.now() - gameStartTime.current) / 1000;
        if (elapsed - lastDeviationSample.current >= 1) {
          lastDeviationSample.current = elapsed;
          // Normalize heading deviation to handle wrap-around at 360°
          let headingDev = f.heading - f.targetHeading;
          if (headingDev > 180) headingDev -= 360;
          if (headingDev < -180) headingDev += 360;
          deviationData.current.push({
            time: elapsed,
            headingDeviation: headingDev,
            altitudeDeviation: f.altitude - f.targetAltitude,
            speedDeviation: f.speed - f.targetSpeed
          });
        }
      }
    }, 50); // 20fps render updates
    
    return () => {
      clearInterval(physicsLoop);
      clearInterval(renderLoop);
    };
  }, [gameState, isPaused, settings]);

  // Keyboard input handling
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (gameState !== 'playing') return;
      
      switch (e.key) {
        case 'ArrowLeft':
          keysPressed.current.left = true;
          break;
        case 'ArrowRight':
          keysPressed.current.right = true;
          break;
        case 'ArrowUp':
          keysPressed.current.up = true;
          break;
        case 'ArrowDown':
          keysPressed.current.down = true;
          break;
        case 'q':
        case 'Q':
        case 'CapsLock':
          // Only allow throttle changes if airspeed is enabled (use effectiveSettingsRef for real test mode)
          if (effectiveSettingsRef.current?.airspeed) {
            const newThrust = Math.min(20, throttleDelayRef.current.pendingThrust + 1);
            throttleDelayRef.current.pendingThrust = newThrust;
            throttleDelayRef.current.lastChangeTime = Date.now();
            setThrust(newThrust); // Update UI immediately to show pending
            setThrottlePlusPressed(true); // Visual feedback
          }
          break;
        case 'a':
        case 'A':
        case 'Control':
          // Only allow throttle changes if airspeed is enabled (use effectiveSettingsRef for real test mode)
          if (effectiveSettingsRef.current?.airspeed) {
            const newThrust = Math.max(0, throttleDelayRef.current.pendingThrust - 1);
            throttleDelayRef.current.pendingThrust = newThrust;
            throttleDelayRef.current.lastChangeTime = Date.now();
            setThrust(newThrust); // Update UI immediately to show pending
            setThrottleMinusPressed(true); // Visual feedback
          }
          break;
        case ' ':
        case 'c':
        case 'C':
          // Green button - even sequence
          if (handleAuditoryResponseRef.current) {
            handleAuditoryResponseRef.current('green');
          }
          setGreenButtonPressed(true); // Visual feedback
          break;
        case 'Alt':
        case 'x':
        case 'X':
          // Red button - odd sequence
          if (handleAuditoryResponseRef.current) {
            handleAuditoryResponseRef.current('red');
          }
          setRedButtonPressed(true); // Visual feedback
          e.preventDefault();
          break;
        default:
          break;
      }
      
      updateInputFromKeys();
    };
    
    const handleKeyUp = (e) => {
      switch (e.key) {
        case 'ArrowLeft':
          keysPressed.current.left = false;
          break;
        case 'ArrowRight':
          keysPressed.current.right = false;
          break;
        case 'ArrowUp':
          keysPressed.current.up = false;
          break;
        case 'ArrowDown':
          keysPressed.current.down = false;
          break;
        case 'q':
        case 'Q':
        case 'CapsLock':
          setThrottlePlusPressed(false);
          break;
        case 'a':
        case 'A':
        case 'Control':
          setThrottleMinusPressed(false);
          break;
        case ' ':
        case 'c':
        case 'C':
          setGreenButtonPressed(false);
          break;
        case 'Alt':
        case 'x':
        case 'X':
          setRedButtonPressed(false);
          break;
        default:
          break;
      }
      
      updateInputFromKeys();
    };
    
    // Debug mode toggle: Shift+O
    const handleDebugToggle = (e) => {
      if (isStaff && e.shiftKey && (e.key === 'o' || e.key === 'O')) {
        setDebugMode(prev => !prev);
        e.preventDefault();
      }
    };
    
    const updateInputFromKeys = () => {
      let x = 0;
      let y = 0;
      
      if (keysPressed.current.left) x -= 1;
      if (keysPressed.current.right) x += 1;
      // Up arrow = nose down = descend = negative Y (reduces altitude)
      // Down arrow = nose up = climb = positive Y (increases altitude)
      if (keysPressed.current.up) y -= 1;
      if (keysPressed.current.down) y += 1;
      
      // Update ref directly for physics loop
      flightRef.current.inputX = x;
      flightRef.current.inputY = y;
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('keydown', handleDebugToggle);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('keydown', handleDebugToggle);
    };
  }, [gameState, isStaff]);

  // Generate the full sequence when game starts
  useEffect(() => {
    if (!audioEnabled || gameState !== 'playing') return;
    
    // Calculate duration based on mode
    let durationSeconds;
    if (isRealTestMode) {
      durationSeconds = REAL_TEST_TASK_DURATION; // 75 seconds per task
    } else {
      durationSeconds = settings?.duration || 180; // duration is already in seconds from setup
    }
    const totalNumbers = Math.floor(durationSeconds / 7); // One number every 7 seconds
    
    // Helper function to generate a sequence with at least one 3-number trigger
    const generateSequenceWithTrigger = () => {
      const sequence = [];
      let lastNumber = null;
      
      // Ensure at least one 3-number odd or even sequence exists
      // For short durations, we'll force a trigger early in the sequence
      const minTriggers = 1;
      const triggerPosition = Math.min(2, totalNumbers - 1); // Position where we'll force a trigger (index 2 = 3rd number)
      const forceOdd = Math.random() < 0.5; // Randomly choose odd or even for forced trigger
      
      for (let i = 0; i < totalNumbers; i++) {
        let availableNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9].filter(n => n !== lastNumber);
        
        // Force a 3-number sequence at the beginning to guarantee at least one trigger
        if (i <= triggerPosition && totalNumbers >= 3) {
          if (forceOdd) {
            availableNumbers = availableNumbers.filter(n => n % 2 === 1); // Force odd
          } else {
            availableNumbers = availableNumbers.filter(n => n % 2 === 0); // Force even
          }
        } else if (sequence.length >= 3) {
          // Check if last 3 numbers are all odd or all even - if so, force opposite parity
          const lastThree = sequence.slice(-3);
          const allOdd = lastThree.every(n => n % 2 === 1);
          const allEven = lastThree.every(n => n % 2 === 0);
          
          if (allOdd) {
            // Must pick an even number to break the streak
            availableNumbers = availableNumbers.filter(n => n % 2 === 0);
          } else if (allEven) {
            // Must pick an odd number to break the streak
            availableNumbers = availableNumbers.filter(n => n % 2 === 1);
          }
        }
        
        const newNum = availableNumbers[Math.floor(Math.random() * availableNumbers.length)];
        sequence.push(newNum);
        lastNumber = newNum;
      }
      
      return sequence;
    };
    
    const sequence = generateSequenceWithTrigger();
    
    // Analyze sequence to find trigger points (3 consecutive odd or even)
    const analyzedSequence = sequence.map((num, idx) => {
      let triggerType = null;
      if (idx >= 2) {
        const lastThree = [sequence[idx - 2], sequence[idx - 1], num];
        const allOdd = lastThree.every(n => n % 2 === 1);
        const allEven = lastThree.every(n => n % 2 === 0);
        if (allOdd) triggerType = 'red';
        else if (allEven) triggerType = 'green';
      }
      return { number: num, triggerType };
    });
    
    // Count total expected responses
    const totalExpected = analyzedSequence.filter(item => item.triggerType !== null).length;
    
    setPreGeneratedSequence(analyzedSequence);
    setCurrentNumberIndex(0);
    setAuditoryStats({ expectedSoFar: 0, totalExpected });
    setAuditoryResults({ correct: 0, missed: 0, falsePositives: 0 });
    setNumberSequence([]);
    expectedButtonRef.current = null;
    setExpectedButton(null);
    
  }, [audioEnabled, gameState, settings?.duration, currentTaskIndex]);

  // Auditory task - speak numbers from pre-generated sequence
  useEffect(() => {
    if (!audioEnabled || gameState !== 'playing' || isPaused || preGeneratedSequence.length === 0) return;
    
    const speakCurrentNumber = () => {
      setCurrentNumberIndex(prevIndex => {
        if (prevIndex >= preGeneratedSequence.length) return prevIndex;
        
        // Check if user missed the previous sequence before speaking new number
        if (expectedButtonRef.current) {
          setAuditoryResults(prev => ({ ...prev, missed: prev.missed + 1 }));
          const missedType = expectedButtonRef.current === 'red' ? 'odd' : 'even';
          setAuditoryFeedback({ type: 'missed', message: `Missed ${missedType} response` });
          setTimeout(() => setAuditoryFeedback(null), 2000);
          expectedButtonRef.current = null;
          setExpectedButton(null);
        }
        
        const currentItem = preGeneratedSequence[prevIndex];
        const newNum = currentItem.number;
        
        // Update the live sequence for display
        setNumberSequence(prev => [...prev, newNum]);
        
        // Set expected button if this is a trigger point
        if (currentItem.triggerType) {
          setExpectedButton(currentItem.triggerType);
          expectedButtonRef.current = currentItem.triggerType;
          setAuditoryStats(prev => ({ ...prev, expectedSoFar: prev.expectedSoFar + 1 }));
        }
        
        // Speak the number
        if ('speechSynthesis' in window) {
          window.speechSynthesis.cancel(); // Cancel any pending speech
          const utterance = new SpeechSynthesisUtterance(newNum.toString());
          utterance.rate = 0.8; // Faster speech rate (was 0.5)
          utterance.pitch = 1;
          window.speechSynthesis.speak(utterance);
        }
        
        return prevIndex + 1;
      });
    };
    
    // Speak first number immediately
    speakCurrentNumber();
    
    // Then speak every 7 seconds
    const audioInterval = setInterval(speakCurrentNumber, 7000);
    
    return () => {
      clearInterval(audioInterval);
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, [audioEnabled, gameState, isPaused, preGeneratedSequence]);

  const handleAuditoryResponse = useCallback((button) => {
    if (!audioEnabled) return;
    
    // If less than 3 numbers spoken, any button press is incorrect
    if (numberSequence.length < 3) {
      setAuditoryResults(prev => ({ ...prev, falsePositives: prev.falsePositives + 1 }));
      setAuditoryFeedback({ type: 'incorrect', message: 'Incorrect response' });
      setTimeout(() => setAuditoryFeedback(null), 2000);
      return;
    }
    
    const lastThree = numberSequence.slice(-3);
    const allOdd = lastThree.every(n => n % 2 === 1);
    const allEven = lastThree.every(n => n % 2 === 0);
    
    if (button === 'red' && allOdd) {
      setAuditoryResults(prev => ({ ...prev, correct: prev.correct + 1 }));
      expectedButtonRef.current = null; // Mark as responded
      setExpectedButton(null);
      setAuditoryFeedback({ type: 'correct', message: 'Correct response' });
      setTimeout(() => setAuditoryFeedback(null), 2000);
    } else if (button === 'green' && allEven) {
      setAuditoryResults(prev => ({ ...prev, correct: prev.correct + 1 }));
      expectedButtonRef.current = null; // Mark as responded
      setExpectedButton(null);
      setAuditoryFeedback({ type: 'correct', message: 'Correct response' });
      setTimeout(() => setAuditoryFeedback(null), 2000);
    } else {
      setAuditoryResults(prev => ({ ...prev, falsePositives: prev.falsePositives + 1 }));
      setAuditoryFeedback({ type: 'incorrect', message: 'Incorrect response' });
      setTimeout(() => setAuditoryFeedback(null), 2000);
    }
  }, [audioEnabled, numberSequence]);
  
  // Keep ref updated for keyboard effect access
  useEffect(() => {
    handleAuditoryResponseRef.current = handleAuditoryResponse;
  }, [handleAuditoryResponse]);

  // Draw compass - reads from flightRef for stable function reference
  const drawCompass = useCallback(() => {
    const canvas = compassCanvasRef.current;
    if (!canvas) return;
    
    const f = flightRef.current;
    const ctx = canvas.getContext('2d');
    const size = canvas.width;
    const center = size / 2;
    const radius = size / 2 - 10;
    
    ctx.clearRect(0, 0, size, size);
    
    // Thin border #4d4d4d
    ctx.strokeStyle = '#4d4d4d';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(center, center, radius + 8, 0, Math.PI * 2);
    ctx.stroke();
    
    // Instrument background #404040
    ctx.fillStyle = '#404040';
    ctx.beginPath();
    ctx.arc(center, center, radius + 7, 0, Math.PI * 2);
    ctx.fill();
    
    // Inner light gray face (like reference image)
    ctx.fillStyle = '#404040';
    ctx.beginPath();
    ctx.arc(center, center, radius, 0, Math.PI * 2);
    ctx.fill();
    
    // Rotating compass card
    ctx.save();
    ctx.translate(center, center);
    ctx.rotate(-f.heading * Math.PI / 180);
    
    // Text and pointer color #ffffff
    ctx.strokeStyle = '#ffffff';
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Draw tick marks and labels
    for (let i = 0; i < 360; i += 5) {
      const angle = i * Math.PI / 180;
      let innerR = radius - 8;
      let lineWidth = 1;
      
      if (i % 30 === 0) {
        innerR = radius - 20;
        lineWidth = 2;
      } else if (i % 10 === 0) {
        innerR = radius - 14;
      }
      
      ctx.beginPath();
      ctx.moveTo(Math.sin(angle) * innerR, -Math.cos(angle) * innerR);
      ctx.lineTo(Math.sin(angle) * (radius - 2), -Math.cos(angle) * (radius - 2));
      ctx.lineWidth = lineWidth;
      ctx.stroke();
      
      if (i % 30 === 0) {
        const labels = { 0: 'N', 90: 'E', 180: 'S', 270: 'W' };
        const label = labels[i] || (i / 10).toString();
        const textR = radius - 32;
        // Rotate text to point toward center
        ctx.save();
        ctx.translate(Math.sin(angle) * textR, -Math.cos(angle) * textR);
        ctx.rotate(angle); // Rotate so text points inward
        ctx.fillText(label, 0, 0);
        ctx.restore();
      }
    }
    ctx.restore();
    
    // Aircraft symbol (#ffffff)
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    // Fuselage
    ctx.beginPath();
    ctx.moveTo(center, center - 30);
    ctx.lineTo(center - 4, center + 18);
    ctx.lineTo(center + 4, center + 18);
    ctx.closePath();
    ctx.fill();
    // Wings
    ctx.beginPath();
    ctx.moveTo(center - 25, center + 5);
    ctx.lineTo(center + 25, center + 5);
    ctx.lineWidth = 4;
    ctx.stroke();
    // Tail
    ctx.beginPath();
    ctx.moveTo(center - 10, center + 18);
    ctx.lineTo(center + 10, center + 18);
    ctx.lineWidth = 3;
    ctx.stroke();
    
    // Target heading (green dot) - on small increment diameter
    if (effectiveSettings?.compass !== 'off') {
      const targetAngle = (f.targetHeading - f.heading) * Math.PI / 180;
      ctx.fillStyle = '#00ff00';
      ctx.beginPath();
      ctx.arc(
        center + Math.sin(targetAngle) * (radius - 5),
        center - Math.cos(targetAngle) * (radius - 5),
        5, 0, Math.PI * 2
      );
      ctx.fill();
    }
  }, [effectiveSettings?.compass]);

  // Draw altimeter - reads from flightRef for stable function reference
  const drawAltimeter = useCallback(() => {
    const canvas = altimeterCanvasRef.current;
    if (!canvas) return;
    
    const f = flightRef.current;
    const ctx = canvas.getContext('2d');
    const size = canvas.width;
    const center = size / 2;
    const radius = size / 2 - 10;
    
    ctx.clearRect(0, 0, size, size);
    
    // Thin border #4d4d4d
    ctx.strokeStyle = '#4d4d4d';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(center, center, radius + 8, 0, Math.PI * 2);
    ctx.stroke();
    
    // Instrument background #404040
    ctx.fillStyle = '#404040';
    ctx.beginPath();
    ctx.arc(center, center, radius + 7, 0, Math.PI * 2);
    ctx.fill();
    
    // Inner light gray face
    ctx.fillStyle = '#404040';
    ctx.beginPath();
    ctx.arc(center, center, radius, 0, Math.PI * 2);
    ctx.fill();
    
    // Text and pointer color #ffffff
    ctx.strokeStyle = '#ffffff';
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Draw scale (0-9) with 5 small increments between each
    for (let i = 0; i < 10; i++) {
      const angle = (i * 36 - 90) * Math.PI / 180;
      
      // Major tick marks
      ctx.beginPath();
      ctx.moveTo(center + Math.cos(angle) * (radius - 15), center + Math.sin(angle) * (radius - 15));
      ctx.lineTo(center + Math.cos(angle) * (radius - 3), center + Math.sin(angle) * (radius - 3));
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Numbers
      const textR = radius - 28;
      ctx.fillText(i.toString(), center + Math.cos(angle) * textR, center + Math.sin(angle) * textR);
      
      // 5 small increments between major marks (36 degrees / 5 = 7.2 degrees each)
      for (let j = 1; j < 5; j++) {
        const smallAngle = ((i * 36 + j * 7.2) - 90) * Math.PI / 180;
        ctx.beginPath();
        ctx.moveTo(center + Math.cos(smallAngle) * (radius - 8), center + Math.sin(smallAngle) * (radius - 8));
        ctx.lineTo(center + Math.cos(smallAngle) * (radius - 3), center + Math.sin(smallAngle) * (radius - 3));
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    }
    
    // "ALT" label - below center
    ctx.font = '10px Arial';
    ctx.fillText('ALT', center, center + radius * 0.45);
    
    // Digital readout box - ABOVE center and bigger
    const boxWidth = 60;
    const boxHeight = 22;
    const boxY = center - 25; // Move above center
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(center - boxWidth/2, boxY - boxHeight/2, boxWidth, boxHeight);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 1;
    ctx.strokeRect(center - boxWidth/2, boxY - boxHeight/2, boxWidth, boxHeight);
    
    // Digital altitude display - bigger font
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 16px monospace';
    const altStr = Math.round(f.altitude).toString().padStart(5, '0');
    ctx.fillText(altStr, center, boxY);
    
    // Long pointer (hundreds) - #ffffff
    const hundredsAngle = ((f.altitude % 1000) / 1000 * 360 - 90) * Math.PI / 180;
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(center, center);
    ctx.lineTo(center + Math.cos(hundredsAngle) * (radius - 35), center + Math.sin(hundredsAngle) * (radius - 35));
    ctx.stroke();
    
    // Center dot
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(center, center, 5, 0, Math.PI * 2);
    ctx.fill();
    
    // Target altitude (green dot) - on small increment diameter
    if (effectiveSettings?.altimeter !== 'off') {
      const targetAngle = ((f.targetAltitude % 1000) / 1000 * 360 - 90) * Math.PI / 180;
      ctx.fillStyle = '#00ff00';
      ctx.beginPath();
      ctx.arc(
        center + Math.cos(targetAngle) * (radius - 5),
        center + Math.sin(targetAngle) * (radius - 5),
        5, 0, Math.PI * 2
      );
      ctx.fill();
    }
  }, [effectiveSettings?.altimeter]);

  // Draw speed indicator - reads from flightRef for stable function reference
  // ASI spans 40-260 kts with specific angle segments from image
  const drawSpeedIndicator = useCallback(() => {
    const canvas = speedCanvasRef.current;
    if (!canvas) return;
    
    const f = flightRef.current;
    const ctx = canvas.getContext('2d');
    const size = canvas.width;
    const center = size / 2;
    const radius = size / 2 - 8;
    
    ctx.clearRect(0, 0, size, size);
    
    // Thin border #4d4d4d
    ctx.strokeStyle = '#4d4d4d';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(center, center, radius + 6, 0, Math.PI * 2);
    ctx.stroke();
    
    // Instrument background #404040
    ctx.fillStyle = '#404040';
    ctx.beginPath();
    ctx.arc(center, center, radius + 5, 0, Math.PI * 2);
    ctx.fill();
    
    // Inner light gray face (like reference image)
    ctx.fillStyle = '#404040';
    ctx.beginPath();
    ctx.arc(center, center, radius - 2, 0, Math.PI * 2);
    ctx.fill();
    
    // ASI angles from reference image:
    // Total sweep from 40 to 260 kts
    // Looking at the image: 40kts at ~48° from top-right, 260kts at ~48° from top-left
    // The dial goes clockwise from 40 to 260
    // Start at 48° (from vertical), sweep ~264° total (48+48+72+72+24 = 264 but let's calculate properly)
    // From image: 40kts is at about 48° clockwise from top
    // 260kts is at about 312° (or -48° from top going counter-clockwise)
    
    const minSpeed = 40;
    const maxSpeed = 260;
    const speedRange = maxSpeed - minSpeed; // 220 kts
    
    // From the reference: start at 48° from top (clockwise), end at 312° (or -48°)
    // That's a sweep of 264° going clockwise
    const startAngleDeg = 48;  // 40 kts position (degrees from top, clockwise)
    const sweepAngleDeg = 264; // total sweep
    
    // Helper to convert speed to angle (in radians, 0 = top, clockwise positive)
    const speedToAngle = (spd) => {
      const fraction = (spd - minSpeed) / speedRange;
      const angleDeg = startAngleDeg + fraction * sweepAngleDeg;
      return (angleDeg - 90) * Math.PI / 180; // Convert to canvas coords (0 = right)
    };
    
    // Draw colored outer bands with exact colors from spec
    const drawArc = (startSpd, endSpd, color) => {
      const start = speedToAngle(startSpd);
      const end = speedToAngle(endSpd);
      ctx.beginPath();
      ctx.arc(center, center, radius - 4, start, end);
      ctx.strokeStyle = color;
      ctx.lineWidth = 10;
      ctx.stroke();
    };
    
    // Bands from spec - CORRECTED ranges:
    // 40-60 kts: EMPTY (no band)
    
    // Looking at reference image: there are TWO concentric bands
    // OUTER band (green #00aa00): 60-160 kts
    // INNER band (white #fdfdfd): 60-120 kts (white stripe inside green)
    
    // Draw colored arcs - reduced opacity only
    const arcRadius = radius - 8;
    const arcWidth = 10;
    ctx.globalAlpha = 0.8; // Reduce visibility
    
    // Draw GREEN band (60-160 kts)
    ctx.beginPath();
    ctx.arc(center, center, arcRadius, speedToAngle(60), speedToAngle(160));
    ctx.strokeStyle = '#00aa00';
    ctx.lineWidth = arcWidth;
    ctx.stroke();
    
    // Draw WHITE inner band (60-120 kts) - narrower, on top of green
    ctx.beginPath();
    ctx.arc(center, center, arcRadius - 4, speedToAngle(60), speedToAngle(120));
    ctx.strokeStyle = '#fdfdfd';
    ctx.lineWidth = 4;
    ctx.stroke();
    
    // Yellow band #c3c33d 160-180kts
    ctx.beginPath();
    ctx.arc(center, center, arcRadius, speedToAngle(160), speedToAngle(180));
    ctx.strokeStyle = '#c3c33d';
    ctx.lineWidth = arcWidth;
    ctx.stroke();
    
    // Orange band #c0660d 180-220kts
    ctx.beginPath();
    ctx.arc(center, center, arcRadius, speedToAngle(180), speedToAngle(220));
    ctx.strokeStyle = '#c0660d';
    ctx.lineWidth = arcWidth;
    ctx.stroke();
    
    // Red band 220-240kts
    ctx.beginPath();
    ctx.arc(center, center, arcRadius, speedToAngle(220), speedToAngle(240));
    ctx.strokeStyle = '#c22323';
    ctx.lineWidth = arcWidth;
    ctx.stroke();
    
    ctx.globalAlpha = 1.0; // Reset alpha
    
    // Text and pointer color - WHITE for better visibility
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#ffffff';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Draw tick marks and numbers for every 20 kts
    for (let spd = 40; spd <= 260; spd += 20) {
      const angle = speedToAngle(spd);
      
      // Major tick marks - original length
      ctx.beginPath();
      ctx.moveTo(center + Math.cos(angle) * (radius - 2), center + Math.sin(angle) * (radius - 2));
      ctx.lineTo(center + Math.cos(angle) * (radius - 16), center + Math.sin(angle) * (radius - 16));
      ctx.lineWidth = 2;
      ctx.stroke();
      
      // Numbers
      const textR = radius - 32;
      ctx.fillText(spd.toString(), center + Math.cos(angle) * textR, center + Math.sin(angle) * textR);
    }
    
    // Medium tick marks for 10 kts intervals
    for (let spd = 40; spd < 260; spd += 10) {
      if (spd % 20 !== 0) {
        const angle = speedToAngle(spd);
        ctx.beginPath();
        ctx.moveTo(center + Math.cos(angle) * (radius - 2), center + Math.sin(angle) * (radius - 2));
        ctx.lineTo(center + Math.cos(angle) * (radius - 10), center + Math.sin(angle) * (radius - 10));
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    }
    
    // Small tick marks for 5 kts intervals
    for (let spd = 40; spd <= 260; spd += 5) {
      if (spd % 10 !== 0) {
        const angle = speedToAngle(spd);
        ctx.beginPath();
        ctx.moveTo(center + Math.cos(angle) * (radius - 2), center + Math.sin(angle) * (radius - 2));
        ctx.lineTo(center + Math.cos(angle) * (radius - 7), center + Math.sin(angle) * (radius - 7));
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    }
    
    // "KNOTS" and "AIRSPEED" labels
    ctx.font = 'bold 12px Arial';
    ctx.fillText('KNOTS', center, center - 25);
    ctx.font = '10px Arial';
    ctx.fillText('AIRSPEED', center, center - 12);
    
    // Pointer - WHITE needle, thin but not super pointy
    const pointerAngle = speedToAngle(f.speed);
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    // Needle shape - thin with slightly rounded tip
    ctx.moveTo(center + Math.cos(pointerAngle) * (radius - 20), center + Math.sin(pointerAngle) * (radius - 20)); // tip
    ctx.lineTo(center + Math.cos(pointerAngle + 0.04) * (radius - 30), center + Math.sin(pointerAngle + 0.04) * (radius - 30)); // widen slightly
    ctx.lineTo(center + Math.cos(pointerAngle + 0.06) * 15, center + Math.sin(pointerAngle + 0.06) * 15); // base
    ctx.lineTo(center + Math.cos(pointerAngle - 0.06) * 15, center + Math.sin(pointerAngle - 0.06) * 15); // base
    ctx.lineTo(center + Math.cos(pointerAngle - 0.04) * (radius - 30), center + Math.sin(pointerAngle - 0.04) * (radius - 30)); // widen slightly
    ctx.closePath();
    ctx.fill();
    
    // Center dot
    ctx.beginPath();
    ctx.arc(center, center, 8, 0, Math.PI * 2);
    ctx.fill();
    
    // Target speed (green dot) - on small increment diameter
    if (effectiveSettings?.airspeed && effectiveSettings?.airspeed !== 'off') {
      const targetAngle = speedToAngle(f.targetSpeed);
      ctx.fillStyle = '#00ff00';
      ctx.beginPath();
      ctx.arc(
        center + Math.cos(targetAngle) * (radius - 10),
        center + Math.sin(targetAngle) * (radius - 10),
        6, 0, Math.PI * 2
      );
      ctx.fill();
    }
  }, [effectiveSettings?.airspeed]);

  // Draw clock - consistent colors across all instruments
  const drawClock = useCallback(() => {
    const canvas = clockCanvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const size = canvas.width;
    const center = size / 2;
    const radius = size / 2 - 8;
    
    ctx.clearRect(0, 0, size, size);
    
    // Thin border #4d4d4d
    ctx.strokeStyle = '#4d4d4d';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(center, center, radius + 6, 0, Math.PI * 2);
    ctx.stroke();
    
    // Instrument background #404040
    ctx.fillStyle = '#404040';
    ctx.beginPath();
    ctx.arc(center, center, radius + 5, 0, Math.PI * 2);
    ctx.fill();
    
    // Inner light gray face
    ctx.fillStyle = '#404040';
    ctx.beginPath();
    ctx.arc(center, center, radius, 0, Math.PI * 2);
    ctx.fill();
    
    // Get elapsed time for single pointer (starts at 60/top, rotates once per minute)
    // Use performance.now() for stable high-resolution timing
    let elapsedSeconds = 0;
    if (clockStartTime.current) {
      elapsedSeconds = (performance.now() - clockStartTime.current) / 1000;
    }
    // elapsedSeconds already includes fractional part, use directly for smooth rotation
    const smoothSeconds = elapsedSeconds % 60;
    
    // Draw second marks (60 marks around the dial) - WHITE for visibility
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = '#ffffff';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    for (let i = 0; i < 60; i++) {
      const angle = (i * 6 - 90) * Math.PI / 180;
      if (i % 5 === 0) {
        // Major marks (every 5 seconds)
        ctx.beginPath();
        ctx.moveTo(center + Math.cos(angle) * (radius - 15), center + Math.sin(angle) * (radius - 15));
        ctx.lineTo(center + Math.cos(angle) * (radius - 4), center + Math.sin(angle) * (radius - 4));
        ctx.lineWidth = 2;
        ctx.stroke();
        
        // Numbers (0, 5, 10, 15... or 60, 5, 10...)
        const num = i === 0 ? 60 : i;
        const textR = radius - 26;
        ctx.fillText(num.toString(), center + Math.cos(angle) * textR, center + Math.sin(angle) * textR);
      } else {
        // Minor marks
        ctx.beginPath();
        ctx.moveTo(center + Math.cos(angle) * (radius - 8), center + Math.sin(angle) * (radius - 8));
        ctx.lineTo(center + Math.cos(angle) * (radius - 4), center + Math.sin(angle) * (radius - 4));
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    }
    
    // Single pointer - rotates once per minute (smooth) - WHITE
    const pointerAngle = (smoothSeconds * 6 - 90) * Math.PI / 180;
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(center, center);
    ctx.lineTo(center + Math.cos(pointerAngle) * (radius - 18), center + Math.sin(pointerAngle) * (radius - 18));
    ctx.stroke();
    
    // Red tip on pointer
    ctx.strokeStyle = '#cc0000';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(center + Math.cos(pointerAngle) * (radius - 18), center + Math.sin(pointerAngle) * (radius - 18));
    ctx.lineTo(center + Math.cos(pointerAngle) * (radius - 8), center + Math.sin(pointerAngle) * (radius - 8));
    ctx.stroke();
    
    // Center dot - WHITE
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(center, center, 4, 0, Math.PI * 2);
    ctx.fill();
  }, []);

  // Render loop for instruments (not clock) - throttled to 30fps
  useEffect(() => {
    if (gameState !== 'playing' && gameState !== 'briefing') return;
    
    const instrumentRenderLoop = setInterval(() => {
      drawCompass();
      drawAltimeter();
      drawSpeedIndicator();
    }, 33); // ~30fps for canvas rendering
    
    // Initial draw
    drawCompass();
    drawAltimeter();
    drawSpeedIndicator();
    
    return () => clearInterval(instrumentRenderLoop);
  }, [gameState, drawCompass, drawAltimeter, drawSpeedIndicator]);

  // Store drawClock in a ref to avoid effect recreation
  const drawClockRef = useRef(drawClock);
  drawClockRef.current = drawClock;

  // Separate render loop for clock using requestAnimationFrame
  useEffect(() => {
    let animationFrameId;
    let lastDrawTime = 0;
    
    const animate = (timestamp) => {
      // Throttle to ~30fps (33ms between frames)
      if (timestamp - lastDrawTime >= 33) {
        drawClockRef.current();
        lastDrawTime = timestamp;
      }
      animationFrameId = requestAnimationFrame(animate);
    };
    
    animationFrameId = requestAnimationFrame(animate);
    
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, []); // Empty dependency - runs once on mount

  // Handle game finish
  useEffect(() => {
    if (gameState === 'finished') {
      saveResults();
    }
  }, [gameState]);

  // Calculate task score with new scoring logic:
  // - Each instrument contributes 25% (compass, altimeter, airspeed, auditory)
  // - If instrument is OFF, score is 0%
  // - If instrument is CONSTANT, multiply by 0.8
  // - Auditory score = (correct / total_expected) * 100
  const calculateTaskScore = (taskSettings, deviations, auditoryData, auditoryStatsData) => {
    const headingDeviations = deviations.map(d => Math.abs(d.headingDeviation));
    const altitudeDeviations = deviations.map(d => Math.abs(d.altitudeDeviation));
    const speedDeviations = deviations.map(d => Math.abs(d.speedDeviation));
    
    const avgHeadingDev = headingDeviations.length > 0 ? headingDeviations.reduce((a, b) => a + b, 0) / headingDeviations.length : 0;
    const avgAltitudeDev = altitudeDeviations.length > 0 ? altitudeDeviations.reduce((a, b) => a + b, 0) / altitudeDeviations.length : 0;
    const avgSpeedDev = speedDeviations.length > 0 ? speedDeviations.reduce((a, b) => a + b, 0) / speedDeviations.length : 0;
    
    // Calculate raw scores (0-100 based on deviation)
    let headingScore = 0;
    let altitudeScore = 0;
    let speedScore = 0;
    let auditoryScore = 0;
    
    // Compass score
    if (taskSettings.compass === 'off') {
      headingScore = 0;
    } else {
      const rawScore = Math.max(0, 100 - avgHeadingDev * 2);
      headingScore = taskSettings.compass === 'constant' ? rawScore * 0.8 : rawScore;
    }
    
    // Altimeter score
    if (taskSettings.altimeter === 'off') {
      altitudeScore = 0;
    } else {
      const rawScore = Math.max(0, 100 - avgAltitudeDev / 5);
      altitudeScore = taskSettings.altimeter === 'constant' ? rawScore * 0.8 : rawScore;
    }
    
    // Airspeed score
    if (!taskSettings.airspeed) {
      speedScore = 0;
    } else {
      speedScore = Math.max(0, 100 - avgSpeedDev * 2);
    }
    
    // Auditory score (percentage of correct responses)
    if (!taskSettings.audio) {
      auditoryScore = 0;
    } else {
      const totalExpected = auditoryStatsData?.totalExpected || 0;
      if (totalExpected > 0) {
        auditoryScore = (auditoryData.correct / totalExpected) * 100;
      } else {
        auditoryScore = auditoryData.correct > 0 ? 100 : 0;
      }
    }
    
    // Final score is average of all 4 components (including 0s for inactive)
    const finalScore = (headingScore + altitudeScore + speedScore + auditoryScore) / 4;
    
    return {
      headingScore,
      altitudeScore,
      speedScore,
      auditoryScore,
      finalScore
    };
  };

  const saveResults = async () => {
    // For real test mode, include the last task's results and save all tasks
    if (isRealTestMode) {
      const lastTask = PREDEFINED_TASKS[currentTaskIndex];
      
      // Capture last task's deviation data
      const lastTaskDeviationData = [...deviationData.current];
      
      // Use refs to get current auditory data (refs always have latest values)
      const capturedAuditoryResults = { ...auditoryResultsRef.current };
      const capturedAuditoryStats = { ...auditoryStatsRef.current };
      
      const lastTaskScores = calculateTaskScore(
        lastTask,
        lastTaskDeviationData,
        capturedAuditoryResults,
        capturedAuditoryStats
      );
      
      const allTaskResults = [...realTestResults, {
        taskId: lastTask.id,
        taskTitle: lastTask.title,
        ...lastTaskScores,
        auditoryResults: capturedAuditoryResults,
        auditoryStats: capturedAuditoryStats,
        deviationData: lastTaskDeviationData
      }];
      
      // New score calculation logic:
      // Compass: sum of all tasks / 10.2
      // Altimeter: sum of all tasks / 9
      // Airspeed: sum of all tasks / 6
      // Audio: correct / total occurrences * 100
      // Final: average of the 4 percentages
      
      const compassSum = allTaskResults.reduce((sum, t) => sum + (t.headingScore || 0), 0);
      const altimeterSum = allTaskResults.reduce((sum, t) => sum + (t.altitudeScore || 0), 0);
      const airspeedSum = allTaskResults.reduce((sum, t) => sum + (t.speedScore || 0), 0);
      
      // Sum up auditory results
      const totalAuditoryCorrect = allTaskResults.reduce((sum, t) => sum + (t.auditoryResults?.correct || 0), 0);
      const totalAuditoryMissed = allTaskResults.reduce((sum, t) => sum + (t.auditoryResults?.missed || 0), 0);
      const totalAuditoryFalsePositives = allTaskResults.reduce((sum, t) => sum + (t.auditoryResults?.falsePositives || 0), 0);
      const totalAuditoryExpected = allTaskResults.reduce((sum, t) => sum + (t.auditoryStats?.totalExpected || 0), 0);
      
      // Calculate percentages with specified divisors
      const compassPercent = Math.min(100, compassSum / 10.2);
      const altimeterPercent = Math.min(100, altimeterSum / 9);
      const airspeedPercent = Math.min(100, airspeedSum / 6);
      const auditoryPercent = totalAuditoryExpected > 0 
        ? Math.min(100, (totalAuditoryCorrect / totalAuditoryExpected) * 100) 
        : 0;
      
      // Final score is average of the 4 percentages
      const overallScore = (compassPercent + altimeterPercent + airspeedPercent + auditoryPercent) / 4;
      
      // Combine all deviation data
      const combinedDeviationData = allTaskResults.flatMap(t => t.deviationData || []).filter((_, i) => i % 10 === 0);
      
      const resultData = {
        subject: subjectId,
        run_mode: 'real_test',
        predefined_task_number: null,
        duration_seconds: REAL_TEST_TASK_DURATION * 12,
        compass_mode: 'changing',  // Real test uses all modes, default to changing
        altimeter_mode: 'changing',
        airspeed_enabled: true,
        turbulence_enabled: true,
        auditory_task_enabled: true,
        final_score: overallScore.toFixed(2),
        heading_score: compassPercent.toFixed(2),
        altitude_score: altimeterPercent.toFixed(2),
        speed_score: airspeedPercent.toFixed(2),
        auditory_correct: totalAuditoryCorrect,
        auditory_missed: totalAuditoryMissed,
        auditory_false_positives: totalAuditoryFalsePositives,
        auditory_total_expected: totalAuditoryExpected,
        deviation_data: combinedDeviationData,
        started_at: new Date(gameStartTime.current).toISOString()
      };
      
      try {
        await apiClient.post('/api/courses/mic-game/runs/', resultData);
      } catch (error) {
        // Failed to save game results
      }
      
      // Navigate to results with all task data
      navigate(`${gamesBase}/mic/${subjectId}/results`, { 
        state: { 
          results: resultData,
          taskResults: allTaskResults,
          isRealTest: true
        } 
      });
      return;
    }
    
    // Regular mode (predefined or custom)
    // Use refs to get current auditory data for consistency
    const regularAuditoryResults = { ...auditoryResultsRef.current };
    const regularAuditoryStats = { ...auditoryStatsRef.current };
    
    const taskScores = calculateTaskScore(
      {
        compass: settings?.compass || 'off',
        altimeter: settings?.altimeter || 'off',
        airspeed: settings?.airspeed || false,
        audio: settings?.audio || false
      },
      deviationData.current,
      regularAuditoryResults,
      regularAuditoryStats
    );
    
    const resultData = {
      subject: subjectId,
      run_mode: settings?.runMode || 'custom',
      predefined_task_number: settings?.predefinedTaskNumber || null,
      duration_seconds: settings?.duration || 180,
      compass_mode: settings?.compass || 'constant',
      altimeter_mode: settings?.altimeter || 'constant',
      airspeed_enabled: settings?.airspeed || false,
      turbulence_enabled: settings?.turbulence || true,
      auditory_task_enabled: settings?.audio || false,
      final_score: taskScores.finalScore.toFixed(2),
      heading_score: taskScores.headingScore.toFixed(2),
      altitude_score: taskScores.altitudeScore.toFixed(2),
      speed_score: taskScores.speedScore.toFixed(2),
      auditory_score: taskScores.auditoryScore.toFixed(2),
      auditory_correct: regularAuditoryResults.correct,
      auditory_missed: regularAuditoryResults.missed,
      auditory_false_positives: regularAuditoryResults.falsePositives,
      auditory_total_expected: regularAuditoryStats.totalExpected,
      deviation_data: deviationData.current.filter((_, i) => i % 10 === 0), // Sample every 10th point
      started_at: new Date(gameStartTime.current).toISOString()
    };
    
    try {
      await apiClient.post('/api/courses/mic-game/runs/', resultData);
    } catch (error) {
      // Failed to save game results
    }
    
    // Navigate to results
    navigate(`${gamesBase}/mic/${subjectId}/results`, { 
      state: { 
        results: resultData,
        deviationData: deviationData.current
      } 
    });
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="h-[calc(100vh-64px)] relative overflow-hidden" style={{ backgroundColor: '#252525' }}>
      {/* Briefing overlay - shown on top of instruments */}
      {gameState === 'briefing' && (() => {
        const currentTaskSettings = isRealTestMode ? PREDEFINED_TASKS[currentTaskIndex] : settings;
        return (
          <div className="absolute inset-0 flex items-center justify-center z-50 bg-black/50">
            <div className="bg-gray-800 rounded-lg p-6 max-w-sm text-center shadow-xl relative">
              {/* Skip button */}
              <button
                onClick={skipBriefing}
                className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
                title="Skip countdown"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
              <h2 className="text-xl font-bold text-white mb-2">Get Ready</h2>
              <p className="text-gray-400 text-sm mb-4">
                {settings?.runMode === 'predefined' && `Task ${settings.predefinedTaskNumber}: ${PREDEFINED_TASKS.find(t => t.id === settings.predefinedTaskNumber)?.title}`}
                {settings?.runMode === 'custom' && 'Custom Session'}
                {isRealTestMode && `Task ${currentTaskIndex + 1}/12: ${PREDEFINED_TASKS[currentTaskIndex]?.title}`}
              </p>
              <div className="text-5xl font-bold text-primary-500 mb-4">
                {briefingCountdown}
              </div>
              <div className="bg-gray-700/50 rounded p-3 mb-4 text-xs text-left">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                  <div className="text-gray-400">Compass:</div>
                  <div className="text-white capitalize">{currentTaskSettings?.compass || 'off'}</div>
                  <div className="text-gray-400">Altimeter:</div>
                  <div className="text-white capitalize">{currentTaskSettings?.altimeter || 'off'}</div>
                  <div className="text-gray-400">Airspeed:</div>
                  <div className="text-white">{currentTaskSettings?.airspeed ? 'On' : 'Off'}</div>
                  <div className="text-gray-400">Turbulence:</div>
                  <div className="text-white">{currentTaskSettings?.turbulence ? 'On' : 'Off'}</div>
                  <div className="text-gray-400">Auditory:</div>
                  <div className="text-white">{currentTaskSettings?.audio ? 'On' : 'Off'}</div>
                </div>
              </div>
              {isRealTestMode && (
                <div className="text-xs text-gray-500 mb-2">
                  75 seconds per task
                </div>
              )}
              <div className="text-[10px] text-gray-500 space-y-0.5">
                {showVirtualJoystick ? (
                  <>
                    <p>Joystick (bottom right): heading and altitude</p>
                    <p>+ / − beside the airspeed gauge: thrust</p>
                  </>
                ) : (
                  <>
                    <p>Arrow Keys: Control heading and altitude</p>
                    <p>Q/A: Increase/Decrease thrust</p>
                  </>
                )}
                {currentTaskSettings?.audio && <p>Space: Green button | X: Red button</p>}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Exit confirmation modal */}
      {showExitConfirm && (
        <div className="absolute inset-0 flex items-center justify-center z-50 bg-black/60">
          <div className="bg-gray-800 rounded-lg p-4 max-w-xs text-center shadow-xl border border-gray-700">
            <p className="text-gray-200 text-sm mb-4">Are you sure you want to exit? Your progress will be lost.</p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setShowExitConfirm(false)}
                className="px-4 py-1.5 bg-gray-600 hover:bg-gray-500 text-white text-xs font-medium rounded"
              >
                Cancel
              </button>
              <button
                onClick={() => navigate(`${gamesBase}/mic/${subjectId}`)}
                className="px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-medium rounded"
              >
                Exit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Timer with controls - top right corner */}
      <div className="absolute top-3 right-3 flex items-center gap-2 z-10">
        {/* Joystick status indicator */}
        <div className="flex items-center gap-1.5 bg-gray-800/80 px-2 py-1 rounded">
          <div className={`w-2 h-2 rounded-full ${joystickConnected ? 'bg-green-500' : 'bg-gray-500'}`} />
          <span className={`text-[10px] font-medium ${joystickConnected ? 'text-green-400' : 'text-gray-400'}`}>
            {joystickConnected ? 'Joystick' : 'No Joystick'}
          </span>
        </div>
        {/* Virtual joystick toggle */}
        <button
          onClick={() => setShowVirtualJoystick(prev => !prev)}
          className={`px-2 py-1 rounded text-[10px] font-medium transition-colors ${
            showVirtualJoystick 
              ? 'bg-blue-600 text-white' 
              : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
          }`}
        >
          {showVirtualJoystick ? 'Touch ✓' : 'Touch'}
        </button>
        <button
          onClick={() => setShowExitConfirm(true)}
          className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded text-xs font-bold"
        >
          Exit
        </button>
        <button
          onClick={() => setIsPaused(!isPaused)}
          className="bg-yellow-500 hover:bg-yellow-600 text-black px-2 py-1 rounded text-xs font-bold"
        >
          {isPaused ? 'Resume' : 'Pause'}
        </button>
        <div className="bg-blue-600 text-white px-3 py-1 rounded text-sm font-mono font-bold">
          {formatTime(timeRemaining)}
        </div>
      </div>

      {/* Debug Panel - Bigger with sliders */}
      {debugMode && (
        <div className="absolute top-2 left-2 bg-black/90 rounded-lg p-4 text-sm text-white font-mono space-y-3 z-10 min-w-[200px]">
          <div className="flex justify-between items-center">
            <span className="text-yellow-400 font-bold text-base">Debug Panel</span>
            <span className={`text-xs ${fpsRef.current.fps >= 15 ? 'text-green-400' : 'text-red-400'}`}>
              {fpsRef.current.fps} FPS
            </span>
          </div>
          
          {/* X-axis slider */}
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span>X-axis (Yaw)</span>
              <span className={inputX > 0 ? 'text-green-400' : inputX < 0 ? 'text-red-400' : 'text-gray-400'}>
                {inputX.toFixed(2)}
              </span>
            </div>
            <div className="h-3 bg-gray-700 rounded relative">
              <div className="absolute top-0 bottom-0 left-1/2 w-0.5 bg-gray-500"></div>
              <div 
                className="absolute top-0 bottom-0 bg-blue-500 rounded"
                style={{
                  left: inputX >= 0 ? '50%' : `${50 + inputX * 50}%`,
                  width: `${Math.abs(inputX) * 50}%`
                }}
              />
            </div>
          </div>
          
          {/* Y-axis slider */}
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span>Y-axis (Pitch)</span>
              <span className={inputY > 0 ? 'text-red-400' : inputY < 0 ? 'text-green-400' : 'text-gray-400'}>
                {inputY.toFixed(2)}
              </span>
            </div>
            <div className="h-3 bg-gray-700 rounded relative">
              <div className="absolute top-0 bottom-0 left-1/2 w-0.5 bg-gray-500"></div>
              <div 
                className="absolute top-0 bottom-0 bg-blue-500 rounded"
                style={{
                  left: inputY >= 0 ? '50%' : `${50 + inputY * 50}%`,
                  width: `${Math.abs(inputY) * 50}%`
                }}
              />
            </div>
          </div>
          
          <div className="border-t border-gray-600 pt-2 space-y-1 text-xs">
            <div className="flex justify-between">
              <span>Heading Rate:</span>
              <span className={headingRate > 0 ? 'text-green-400' : headingRate < 0 ? 'text-red-400' : 'text-gray-400'}>
                {headingRate.toFixed(0)}°/min
              </span>
            </div>
            <div className="flex justify-between">
              <span>V/S:</span>
              <span className={displayVS > 0 ? 'text-green-400' : displayVS < 0 ? 'text-red-400' : 'text-gray-400'}>
                {displayVS.toFixed(0)} fpm
              </span>
            </div>
            <div className="flex justify-between">
              <span>Speed:</span>
              <span>{speed.toFixed(0)} kts</span>
            </div>
            <div className="flex justify-between">
              <span>Thrust:</span>
              <span>{thrust}/20</span>
            </div>
          </div>
          
          {/* Toggles */}
          <div className="border-t border-gray-600 pt-2 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs">Auditory</span>
              <button 
                onClick={() => setAudioEnabled(!audioEnabled)}
                className={`px-2 py-0.5 rounded text-xs font-bold ${audioEnabled ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'}`}
              >
                {audioEnabled ? 'ON' : 'OFF'}
              </button>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs">Turbulence</span>
              <button 
                onClick={() => setTurbulenceEnabled(!turbulenceEnabled)}
                className={`px-2 py-0.5 rounded text-xs font-bold ${turbulenceEnabled ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'}`}
              >
                {turbulenceEnabled ? 'ON' : 'OFF'}
              </button>
            </div>
          </div>
          
          {/* Reset Button */}
          <button 
            onClick={() => {
              const f = flightRef.current;
              f.heading = GAME_CONFIG.startHeading;
              f.altitude = GAME_CONFIG.startAltitude;
              f.speed = GAME_CONFIG.startSpeed;
              f.thrust = GAME_CONFIG.startThrust;
              f.headingRate = 0;
              f.verticalSpeed = 0;
              f.displayVS = 0;
              f.targetHeading = GAME_CONFIG.startHeading;
              f.targetAltitude = GAME_CONFIG.startAltitude;
              f.targetSpeed = GAME_CONFIG.startSpeed;
              throttleDelayRef.current.pendingThrust = GAME_CONFIG.startThrust;
              throttleDelayRef.current.effectiveThrust = GAME_CONFIG.startThrust;
              setThrust(GAME_CONFIG.startThrust);
              setDisplayThrust(GAME_CONFIG.startThrust);
            }}
            className="w-full bg-orange-600 hover:bg-orange-700 text-white px-2 py-1 rounded text-xs font-bold"
          >
            Reset Position
          </button>
          
          <button onClick={() => setDebugMode(false)} className="text-gray-400 hover:text-white text-xs mt-2">Hide Debug</button>
          
          {/* Pre-generated Auditory Sequence Display */}
          {audioEnabled && preGeneratedSequence.length > 0 && (
            <div className="border-t border-gray-600 pt-2 mt-2">
              <div className="text-xs text-yellow-400 mb-1">Auditory Sequence:</div>
              <div className="flex flex-wrap gap-1 max-w-[180px]">
                {preGeneratedSequence.map((item, idx) => (
                  <span 
                    key={idx}
                    className={`text-xs px-1 rounded ${
                      idx < currentNumberIndex 
                        ? (item.triggerType === 'red' ? 'bg-red-800 text-red-200' : item.triggerType === 'green' ? 'bg-green-800 text-green-200' : 'bg-gray-700 text-gray-400')
                        : idx === currentNumberIndex - 1
                          ? 'bg-white text-black font-bold'
                          : (item.triggerType === 'red' ? 'text-red-400 font-bold' : item.triggerType === 'green' ? 'text-green-400 font-bold' : 'text-gray-300')
                    }`}
                  >
                    {item.number}
                  </span>
                ))}
              </div>
              <div className="text-xs text-gray-400 mt-1">
                Progress: {currentNumberIndex}/{preGeneratedSequence.length}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Main Instrument Panel - FIXED positions */}
      {/* Using fixed pixel positions from center to prevent overlapping at different resolutions */}
      <div
        ref={panelRef}
        className="h-full relative"
        style={{ transform: panelScale < 1 ? `scale(${panelScale})` : undefined, transformOrigin: 'center center' }}
      >
        {/* Compass: 3px left of center, 225px up from center */}
        <div className="absolute flex flex-col items-center" style={{ 
          left: 'calc(50% - 3px - 102px)', 
          top: 'calc(50% - 225px - 130px)' 
        }}>
          {/* Heading and Yaw Rate above compass - debug only */}
          {debugMode && (
            <div className="text-center mb-1 font-mono text-sm">
              <div className="text-white font-bold">{Math.round(heading).toString().padStart(3, '0')}°</div>
              <div className={`text-xs ${headingRate > 0 ? 'text-green-400' : headingRate < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                {headingRate > 0 ? '→ ' : headingRate < 0 ? '← ' : ''}{Math.abs(headingRate).toFixed(0)}°/min
              </div>
            </div>
          )}
          <canvas 
            ref={compassCanvasRef} 
            width={204} 
            height={204}
          />
        </div>

        {/* Throttle [41x204.5px]: 445.9px left of center, 58px up from center */}
        <div className="absolute flex flex-col items-center" style={{ 
          left: 'calc(50% - 445.9px - 20px)', 
          top: 'calc(50% - 58px - 78px)' 
        }}>
          {/* Q hint above plus button */}
          <span className="text-white/30 text-xs font-mono mb-0.5">Q</span>
          {/* Plus button: BG #909090, symbol #1a1a1a - highlights when Q/CapsLock pressed */}
          <button 
            onMouseDown={() => {
              if (!effectiveSettings?.airspeed) return; // Disable when airspeed is off (use effectiveSettings for real test mode)
              setThrottlePlusPressed(true);
              const newThrust = Math.min(20, throttleDelayRef.current.pendingThrust + 1);
              throttleDelayRef.current.pendingThrust = newThrust;
              throttleDelayRef.current.lastChangeTime = Date.now();
              setThrust(newThrust);
            }}
            onMouseUp={() => setThrottlePlusPressed(false)}
            onMouseLeave={() => setThrottlePlusPressed(false)}
            disabled={!effectiveSettings?.airspeed}
            className={`flex items-center justify-center font-bold text-xl transition-all ${throttlePlusPressed ? 'scale-95' : ''} ${!effectiveSettings?.airspeed ? 'opacity-50 cursor-not-allowed' : ''}`}
            style={{ 
              backgroundColor: throttlePlusPressed ? '#606060' : '#909090', 
              color: throttlePlusPressed ? '#ffffff' : '#1a1a1a', 
              width: '41px', 
              height: '24px',
              boxShadow: throttlePlusPressed ? 'inset 0 2px 4px rgba(0,0,0,0.5)' : 'none'
            }}
          >
            +
          </button>
          {/* Throttle background #c0c0c0, filled #0000ff - uses displayThrust (delayed) */}
          <div className="relative" style={{ backgroundColor: '#c0c0c0', width: '41px', height: '156px' }}>
            <div 
              className="absolute bottom-0 left-0 right-0"
              style={{ height: `${(displayThrust / 20) * 100}%`, backgroundColor: '#0000ff' }}
            />
          </div>
          {/* Minus button: BG #909090, symbol #1a1a1a - highlights when A/LCtrl pressed */}
          <button 
            onMouseDown={() => {
              if (!effectiveSettings?.airspeed) return; // Disable when airspeed is off (use effectiveSettings for real test mode)
              setThrottleMinusPressed(true);
              const newThrust = Math.max(0, throttleDelayRef.current.pendingThrust - 1);
              throttleDelayRef.current.pendingThrust = newThrust;
              throttleDelayRef.current.lastChangeTime = Date.now();
              setThrust(newThrust);
            }}
            onMouseUp={() => setThrottleMinusPressed(false)}
            onMouseLeave={() => setThrottleMinusPressed(false)}
            disabled={!effectiveSettings?.airspeed}
            className={`flex items-center justify-center font-bold text-xl transition-all ${throttleMinusPressed ? 'scale-95' : ''} ${!effectiveSettings?.airspeed ? 'opacity-50 cursor-not-allowed' : ''}`}
            style={{ 
              backgroundColor: throttleMinusPressed ? '#606060' : '#909090', 
              color: throttleMinusPressed ? '#ffffff' : '#1a1a1a', 
              width: '41px', 
              height: '24px',
              boxShadow: throttleMinusPressed ? 'inset 0 2px 4px rgba(0,0,0,0.5)' : 'none'
            }}
          >
            −
          </button>
          {/* A hint below minus button */}
          <span className="text-white/30 text-xs font-mono mt-0.5">A</span>
        </div>

        {/* ASI: 303px left of center, 57px up from center */}
        <div className="absolute" style={{ 
          left: 'calc(50% - 303px - 102px)', 
          top: 'calc(50% - 57px - 102px)' 
        }}>
          <canvas 
            ref={speedCanvasRef} 
            width={204} 
            height={204}
            className=""
          />
        </div>

        {/* Auditory Buttons - Under the ASI (airspeed indicator) */}
        {audioEnabled && (
          <div className="absolute flex gap-4 z-50" style={{ 
            left: 'calc(50% - 303px - 30px)', 
            top: 'calc(50% - 57px + 160px)'
          }}>
            <div className="flex flex-col items-center">
              <button
                onMouseDown={() => {
                  setRedButtonPressed(true);
                  handleAuditoryResponse('red');
                }}
                onMouseUp={() => setRedButtonPressed(false)}
                onMouseLeave={() => setRedButtonPressed(false)}
                className={`w-16 h-16 rounded-full shadow-lg transition-all relative flex items-center justify-center ${redButtonPressed ? 'bg-red-800 scale-90' : 'bg-red-600 hover:bg-red-500'}`}
                style={{ boxShadow: redButtonPressed ? 'inset 0 4px 8px rgba(0,0,0,0.6)' : 'inset 0 -4px 8px rgba(0,0,0,0.4)' }}
              >
                <span className="text-white/30 text-xs font-mono">ALT/X</span>
              </button>
              {/* Debug triangle indicator for red button */}
              {debugMode && expectedButton === 'red' && (
                <div className="mt-2 w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-red-400 z-50" />
              )}
            </div>
            <div className="flex flex-col items-center">
              <button
                onMouseDown={() => {
                  setGreenButtonPressed(true);
                  handleAuditoryResponse('green');
                }}
                onMouseUp={() => setGreenButtonPressed(false)}
                onMouseLeave={() => setGreenButtonPressed(false)}
                className={`w-16 h-16 rounded-full shadow-lg transition-all relative flex items-center justify-center ${greenButtonPressed ? 'bg-green-700 scale-90' : 'bg-green-500 hover:bg-green-400'}`}
                style={{ boxShadow: greenButtonPressed ? 'inset 0 4px 8px rgba(0,0,0,0.6)' : 'inset 0 -4px 8px rgba(0,0,0,0.4)' }}
              >
                <span className="text-white/30 text-xs font-mono">Space/C</span>
              </button>
              {/* Debug triangle indicator for green button */}
              {debugMode && expectedButton === 'green' && (
                <div className="mt-2 w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-green-400 z-50" />
              )}
            </div>
          </div>
        )}

        {/* Auditory feedback message - right above the debug deviation text, no background */}
        {audioEnabled && auditoryFeedback && (
          <div className="absolute left-1/2 transform -translate-x-1/2" style={{ bottom: '85px' }}>
            <div className={`text-base font-bold ${
              auditoryFeedback.type === 'correct' ? 'text-green-400' : 
              auditoryFeedback.type === 'missed' ? 'text-yellow-400' : 'text-red-500'
            }`}>
              {auditoryFeedback.message}
            </div>
          </div>
        )}

        {/* Altimeter: 296px right of center, 57px up from center */}
        <div className="absolute flex flex-col items-center" style={{ 
          left: 'calc(50% + 296px - 102px)', 
          top: 'calc(50% - 57px - 130px)' 
        }}>
          {/* Rate of Climb above altimeter - debug only */}
          {debugMode && (
            <div className="text-center mb-1 font-mono text-sm">
              <div className={`font-bold ${displayVS > 0 ? 'text-green-400' : displayVS < 0 ? 'text-red-400' : 'text-gray-400'}`}>
                {displayVS > 0 ? '↑ ' : displayVS < 0 ? '↓ ' : ''}{Math.abs(displayVS).toFixed(0)} fpm
              </div>
            </div>
          )}
          <canvas 
            ref={altimeterCanvasRef} 
            width={204} 
            height={204}
          />
          
        </div>

        {/* Clock: 3px left of center, 105px down from center */}
        {/* 3/1280 = 0.2% left, 105/800 = 13.1% down */}
        <div className="absolute" style={{ 
          left: 'calc(50% - 0.2% - 102px)', 
          top: 'calc(50% + 14.5% - 102px)' 
        }}>
          <canvas 
            ref={clockCanvasRef} 
            width={204} 
            height={204}
          />
        </div>

        {/* Debug deviation display at bottom */}
        {debugMode && (() => {
          const headingDev = flightState.heading - flightState.targetHeading;
          const normalizedHeadingDev = headingDev > 180 ? headingDev - 360 : (headingDev < -180 ? headingDev + 360 : headingDev);
          const altitudeDev = flightState.altitude - flightState.targetAltitude;
          const speedDev = flightState.speed - flightState.targetSpeed;
          
          return (
            <div className="absolute bottom-16 left-0 right-0 text-center">
              <div className="inline-flex gap-6 bg-black/70 px-4 py-2 rounded text-sm font-mono">
                <span className={Math.abs(normalizedHeadingDev) > 30 ? 'text-red-400' : 'text-green-400'}>
                  HDG: {normalizedHeadingDev >= 0 ? '+' : ''}{normalizedHeadingDev.toFixed(1)}°
                </span>
                <span className="text-yellow-400">
                  TR: {flightState.headingRate >= 0 ? '+' : ''}{flightState.headingRate.toFixed(1)}°/s
                </span>
                <span className={Math.abs(altitudeDev) > 100 ? 'text-red-400' : 'text-green-400'}>
                  ALT: {altitudeDev >= 0 ? '+' : ''}{altitudeDev.toFixed(0)}ft
                </span>
                <span className="text-yellow-400">
                  VS: {flightState.verticalSpeed >= 0 ? '+' : ''}{flightState.verticalSpeed.toFixed(0)}fpm
                </span>
                <span className={Math.abs(speedDev) > 10 ? 'text-red-400' : 'text-green-400'}>
                  SPD: {speedDev >= 0 ? '+' : ''}{speedDev.toFixed(1)}kts
                </span>
                {audioEnabled && (
                  <span className="text-purple-400">
                    🎧 ({auditoryResults.correct}/{auditoryStats.expectedSoFar}/{auditoryStats.totalExpected})
                  </span>
                )}
              </div>
            </div>
          );
        })()}

        {/* Warning text at bottom - shows which instruments are outside tolerance */}
        {(() => {
          const warnings = [];
          const headingDev = Math.abs(flightState.heading - flightState.targetHeading);
          const normalizedHeadingDev = headingDev > 180 ? 360 - headingDev : headingDev;
          const altitudeDev = Math.abs(flightState.altitude - flightState.targetAltitude);
          const speedDev = Math.abs(flightState.speed - flightState.targetSpeed);
          
          if (effectiveSettings?.compass !== 'off' && normalizedHeadingDev > 30) warnings.push('compass');
          if (effectiveSettings?.altimeter !== 'off' && altitudeDev > 100) warnings.push('altimeter');
          if (effectiveSettings?.airspeed && effectiveSettings?.airspeed !== 'off' && speedDev > 10) warnings.push('airspeed indicator');
          
          if (warnings.length === 0) return null;
          
          let message = '';
          if (warnings.length === 1) {
            message = `${warnings[0].charAt(0).toUpperCase() + warnings[0].slice(1)} outside its tolerance`;
          } else if (warnings.length === 2) {
            message = `${warnings[0].charAt(0).toUpperCase() + warnings[0].slice(1)} and ${warnings[1]} outside their tolerances`;
          } else {
            message = `${warnings[0].charAt(0).toUpperCase() + warnings[0].slice(1)}, ${warnings[1]} and ${warnings[2]} outside their tolerances`;
          }
          
          return (
            <div className="absolute bottom-8 left-0 right-0 text-center">
              <span className="text-red-500 font-semibold text-lg">{message}</span>
            </div>
          );
        })()}
      </div>

      {/* Virtual joystick: outside the scaled panel so it keeps its full size and never covers a gauge */}
      {showVirtualJoystick && (
        <div className="absolute right-6 bottom-10 z-20">
          <div 
            className="relative w-32 h-32 rounded-full bg-gray-800/80 border-2 border-gray-600 touch-none select-none cursor-pointer"
            onMouseDown={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const centerX = rect.width / 2;
              const centerY = rect.height / 2;
              virtualJoystickRef.current.active = true;
              
              const handleMove = (moveE) => {
                const clientX = moveE.touches ? moveE.touches[0].clientX : moveE.clientX;
                const clientY = moveE.touches ? moveE.touches[0].clientY : moveE.clientY;
                const x = Math.max(-1, Math.min(1, (clientX - rect.left - centerX) / (rect.width / 2)));
                const y = Math.max(-1, Math.min(1, (clientY - rect.top - centerY) / (rect.height / 2)));
                virtualJoystickRef.current.x = x;
                virtualJoystickRef.current.y = y;
                setVirtualJoystickPos({ x, y });
                flightRef.current.inputX = x;
                flightRef.current.inputY = y;
              };
              
              const handleEnd = () => {
                virtualJoystickRef.current.active = false;
                virtualJoystickRef.current.x = 0;
                virtualJoystickRef.current.y = 0;
                setVirtualJoystickPos({ x: 0, y: 0 });
                flightRef.current.inputX = 0;
                flightRef.current.inputY = 0;
                window.removeEventListener('mousemove', handleMove);
                window.removeEventListener('mouseup', handleEnd);
              };
              
              handleMove(e);
              window.addEventListener('mousemove', handleMove);
              window.addEventListener('mouseup', handleEnd);
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              const rect = e.currentTarget.getBoundingClientRect();
              const centerX = rect.width / 2;
              const centerY = rect.height / 2;
              virtualJoystickRef.current.active = true;
              
              const handleMove = (moveE) => {
                const touch = moveE.touches[0];
                const x = Math.max(-1, Math.min(1, (touch.clientX - rect.left - centerX) / (rect.width / 2)));
                const y = Math.max(-1, Math.min(1, (touch.clientY - rect.top - centerY) / (rect.height / 2)));
                virtualJoystickRef.current.x = x;
                virtualJoystickRef.current.y = y;
                setVirtualJoystickPos({ x, y });
                flightRef.current.inputX = x;
                flightRef.current.inputY = y;
              };
              
              const handleEnd = () => {
                virtualJoystickRef.current.active = false;
                virtualJoystickRef.current.x = 0;
                virtualJoystickRef.current.y = 0;
                setVirtualJoystickPos({ x: 0, y: 0 });
                flightRef.current.inputX = 0;
                flightRef.current.inputY = 0;
                window.removeEventListener('touchmove', handleMove);
                window.removeEventListener('touchend', handleEnd);
              };
              
              handleMove(e);
              window.addEventListener('touchmove', handleMove, { passive: false });
              window.addEventListener('touchend', handleEnd);
            }}
          >
            {/* Center crosshair */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-px h-full bg-gray-600/50" />
            </div>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-full h-px bg-gray-600/50" />
            </div>
            {/* Joystick knob */}
            <div 
              className="absolute w-10 h-10 rounded-full bg-gray-500 border-2 border-gray-400 shadow-lg pointer-events-none"
              style={{
                left: `calc(50% + ${virtualJoystickPos.x * 40}px - 20px)`,
                top: `calc(50% + ${virtualJoystickPos.y * 40}px - 20px)`
              }}
            />
            {/* Label */}
            <div className="absolute -bottom-5 left-1/2 transform -translate-x-1/2 text-[10px] text-gray-500 whitespace-nowrap">
              Virtual Joystick
            </div>
          </div>
        </div>
      )}

      {/* On phones the full instrument panel is scaled down a lot; suggest a better setup */}
      {showSmallScreenTip && (
        <div className="absolute top-14 left-1/2 -translate-x-1/2 z-30 w-[calc(100%-2rem)] max-w-sm rounded-lg bg-gray-900/95 border border-gray-700 px-4 py-3 text-sm text-gray-200 shadow-lg flex items-start gap-3">
          <span className="flex-1">MIC uses the full DLR instrument layout. For realistic practice, turn your phone sideways or use a tablet or computer.</span>
          <button onClick={() => setShowSmallScreenTip(false)} className="text-gray-400 hover:text-white px-1" aria-label="Dismiss tip">✕</button>
        </div>
      )}
    </div>
  );
};

// Predefined tasks with full settings
const PREDEFINED_TASKS = [
  { id: 1, title: 'Constant HDG only', compass: 'constant', altimeter: 'off', airspeed: false, turbulence: true, audio: false },
  { id: 2, title: 'Constant ALT only', compass: 'off', altimeter: 'constant', airspeed: false, turbulence: true, audio: false },
  { id: 3, title: 'Constant HDG/ALT', compass: 'constant', altimeter: 'constant', airspeed: false, turbulence: true, audio: false },
  { id: 4, title: 'Changing HDG only', compass: 'changing', altimeter: 'off', airspeed: false, turbulence: true, audio: false },
  { id: 5, title: 'Changing HDG, Constant ALT', compass: 'changing', altimeter: 'constant', airspeed: false, turbulence: true, audio: false },
  { id: 6, title: 'Changing HDG & ALT', compass: 'changing', altimeter: 'changing', airspeed: false, turbulence: true, audio: false },
  { id: 7, title: 'Constant HDG, Changing ALT/SPD', compass: 'constant', altimeter: 'changing', airspeed: true, turbulence: true, audio: false },
  { id: 8, title: 'Changing HDG/SPD, Constant ALT', compass: 'changing', altimeter: 'constant', airspeed: true, turbulence: true, audio: false },
  { id: 9, title: 'Changing HDG/ALT/SPD', compass: 'changing', altimeter: 'changing', airspeed: true, turbulence: true, audio: false },
  { id: 10, title: 'Constant HDG, Changing ALT/SPD W/Audio', compass: 'constant', altimeter: 'changing', airspeed: true, turbulence: true, audio: true },
  { id: 11, title: 'Changing HDG/SPD, Constant ALT W/Audio', compass: 'changing', altimeter: 'constant', airspeed: true, turbulence: true, audio: true },
  { id: 12, title: 'Changing HDG/ALT/SPD W/Audio', compass: 'changing', altimeter: 'changing', airspeed: true, turbulence: true, audio: true },
];

export default MICGame;
